/**
 * earth - a project to visualize global air data.
 *
 * Copyright (c) 2014 Cameron Beccario
 * The MIT License - http://opensource.org/licenses/MIT
 *
 * https://github.com/cambecc/earth
 */
import µ from "./micro";
import globes from "./globes";
import products from "./products";
import * as d3 from "d3";
import _ from "lodash";
import Backbone from 'backbone';
import topojson from "../../lib/topojson/1.1.0/topojson";
import when from "../../lib/when/2.6.0/when";
import mcl from "../../mc/1.0.0/mc";
import mcUtils from "../../mc/1.0.0/mc-utils"
import mcOverlayCs from "../../mc/1.0.0/mc-overlay-cs2d"
import XLSX from "xlsx"
import {geoStitch} from "../../lib/d3.geo.projection/2.4.0/d3-geo-projection.repack";
import staticVariable from "../../component/staticVariable";

export default function earth() {

    var SECOND = 1000;
    var MINUTE = 60 * SECOND;
    var HOUR = 60 * MINUTE;
    var MAX_TASK_TIME = 150;                  // amount of time before a task yields control (millis)
    var MIN_SLEEP_TIME = 10;                  // amount of time a task waits before resuming (millis)
    var MIN_MOVE = 6;                         // slack before a drag operation beings (pixels)
    var MOVE_END_WAIT = 750;                 // time to wait for a move operation to be considered done (millis)

    var OVERLAY_ALPHA = Math.floor(0.47 * 255);  // overlay transparency (on scale [0, 255])
    var INTENSITY_SCALE_STEP = 10;            // step size of particle intensity color scale
    var MAX_PARTICLE_AGE = 100;               // max number of frames a particle is drawn before regeneration
    var PARTICLE_LINE_WIDTH = 1;            // line width of a drawn particle
    var PARTICLE_MULTIPLIER = 7;              // particle count scalar (completely arbitrary--this values looks nice)
    var PARTICLE_REDUCTION = 0.75;            // reduce particle count to this much of normal for mobile devices
    var FRAME_RATE = 40;                      // desired milliseconds per frame

    var NULL_WIND_VECTOR = [NaN, NaN, null];  // singleton for undefined location outside the vector field [u, v, mag]
    var HOLE_VECTOR = [NaN, NaN, null];       // singleton that signifies a hole in the vector field
    var TRANSPARENT_BLACK = [0, 0, 0, 0];     // singleton 0 rgba   231, 93, 34, 119

    var REMAINING = "▫▫▫▫▫▫▫▫▫▫▫▫";   // glyphs for remaining progress bar
    var COMPLETED = "▪▪▪▪▪▪▪▪▪▪▪▪";   // glyphs for completed progress bar

    var earthPath = "/earth.jpg";
    const spacePath = "/space15.jpeg";
    /* sainy menu font size (styles.css 1.75rem -> 1.25 rem)
    var REMAINING = "▫▫▫▫▫▫▫▫▫▫▫▫▫▫▫▫▫▫▫▫▫▫";   // glyphs for remaining progress bar
    var COMPLETED = "▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪";   // glyphs for completed progress bar
    */

    // var START_DATE = "1860/01/01", END_DATE = "2100/12/01"; // 1860/01/01 ~ 2100/12/01
    var TIMESERIES_START_YEAR = 1850;

    var view = µ.view();
    var log = µ.log();

    var earthImage = null;

    /**
     * An object to display various types of messages to the user.
     */
    var report = function () {
        var s = d3.select("#status"), p = d3.select("#progress"), total = REMAINING.length;
        return {
            status: function (msg) {
                return s.text("");
                //return s.classed("bad") ? s : s.text(msg);  // errors are sticky until reset
            },
            error: function (err) {
                var msg = err.status ? err.status + " " + err.message : err;
                switch (err.status) {
                    case -1:
                        msg = "Server Maintenance";
                        break;
                    case 404:
                        msg = "No Data";
                        break;
                }
                log.error(err);
                return s.classed("bad", true).text(msg);
            },
            reset: function () {
                return s.classed("bad", false).text("");
            },
            progress: function (amount) {  // amount of progress to report in the range [0, 1]
                if (0 <= amount && amount < 1) {
                    var i = Math.ceil(amount * total);
                    var bar = COMPLETED.substr(0, i) + REMAINING.substr(0, total - i);
                    // return p.classed("invisible", false).text("");
                    return p.classed("invisible", false).text(bar);
                }
                return p.classed("invisible", true).text("");  // progress complete
            }
        };
    }();

    function newAgent() {
        return µ.newAgent().on({"reject": report.error, "fail": report.error});
    }

    // Construct the page's main internal components:

    var configuration =
        µ.buildConfiguration(globes, products.overlayTypes);  // holds the page's current configuration settings
    var inputController = buildInputController();             // interprets drag/zoom operations
    var meshAgent = newAgent();      // map data for the earth
    var globeAgent = newAgent();     // the model of the globe
    var gridAgent = newAgent();      // the grid of weather data
    var rendererAgent = newAgent();  // the globe SVG renderer
    var fieldAgent = newAgent();     // the interpolated wind vector field
    var animatorAgent = newAgent();  // the wind animator
    var overlayAgent = newAgent();   // color overlay over the animation
    var annualTimeSeriesDataAgent = newAgent(); // timeseries data
    var avgAnnualTimeSeriesDataAgent = newAgent(); // timeseries average annual data
    var quickoverlayAgent = newAgent(); // color overlay over the animation using
    var crossSectionOverlayAgent = newAgent();  // cross section overlay
    var crossSectionOverlayRenderAgent = newAgent(); // cross section overlay renderer

    /**
     * The input controller is an object that translates move operations (drag and/or zoom) into mutations of the
     * current globe's projection, and emits events so other page components can react to these move operations.
     *
     * D3's built-in Zoom behavior is used to bind to the document's drag/zoom events, and the input controller
     * interprets D3's events as move operations on the globe. This method is complicated due to the complex
     * event behavior that occurs during drag and zoom.
     *
     * D3 move operations usually occur as "zoomstart" -> ("zoom")* -> "zoomend" event chain. During "zoom" events
     * the scale and mouse may change, implying a zoom or drag operation accordingly. These operations are quite
     * noisy. What should otherwise be one smooth continuous zoom is usually comprised of several "zoomstart" ->
     * "zoom" -> "zoomend" event chains. A debouncer is used to eliminate the noise by waiting a short period of
     * time to ensure the user has finished the move operation.
     *
     * The "zoom" events may not occur; a simple click operation occurs as: "zoomstart" -> "zoomend". There is
     * additional logic for other corner cases, such as spurious drags which move the globe just a few pixels
     * (most likely unintentional), and the tendency for some touch devices to issue events out of order:
     * "zoom" -> "zoomstart" -> "zoomend".
     *
     * This object emits clean "moveStart" -> ("move")* -> "moveEnd" events for move operations, and "click" events
     * for normal clicks. Spurious moves emit no events.
     */
    function buildInputController() {
        var globe, op = null;
        var quickoverlay;

        /**
         * @returns {Object} an object to represent the state for one move operation.
         */
        function newOp(startMouse, startScale) {
            return {
                type: "click",  // initially assumed to be a click operation
                startMouse: startMouse,
                startScale: startScale,
                manipulator: globe.manipulator(startMouse, startScale)
            };
        }

        function moveStart() {
            if (!globe || !globe.projection) return;
            op = op || newOp(d3.mouse(this), globe.projection.scale());  // a new operation begins
        }

        function move() {
            if (!globe || !globe.projection) return;
            var transform = d3.event.transform || {};
            var currentMouse = d3.mouse(this),
                currentScale = µ.coalesce(transform.k, globe.projection.scale()); //d3.event.scale;

            op = op || newOp(currentMouse, 1);  // Fix bug on some browsers where zoomstart fires out of order.
            // if (op.type === "click" || op.type === "spurious") {
            if (op.type === "click") {
                var distanceMoved = µ.distance(currentMouse, op.startMouse);
                if (currentScale === op.startScale && distanceMoved < MIN_MOVE || isNaN(distanceMoved)) {
                    // to reduce annoyance, ignore op if mouse has barely moved and no zoom is occurring
                    // op.type = distanceMoved > 0 ? "click" : "spurious";
                    return;
                }
                dispatch.trigger("moveStart");
                op.type = "drag";
            }
            if (currentScale != op.startScale) {
                op.type = "zoom";  // whenever a scale change is detected, (stickily) switch to a zoom operation
            }

            // when zooming, ignore whatever the mouse is doing--really cleans up behavior on touch devices
            op.manipulator.move(op.type === "zoom" ? null : currentMouse, currentScale);
            dispatch.trigger("move");
            quickoverlay = quickoverlayAgent.value();
            if (quickoverlay) {
                quickoverlay.quickRender();
            }
        }

        function moveEnd() {
            if (op === null) return;
            op.manipulator.end();
            if (op.type === "click") {
            //     dispatch.trigger("click", op.startMouse, globe.projection.invert(op.startMouse) || []);
            }
            // else if (op.type !== "spurious") {
            else {
                signalEnd();
            }
            op = null;  // the drag/zoom/click operation is over
        }

        var signalEnd = _.debounce(function () {
            if (!op || op.type !== "drag" && op.type !== "zoom") {
                configuration.save({orientation: globe.orientation()}, {source: "moveEnd"});
                dispatch.trigger("moveEnd");
            }
        }, MOVE_END_WAIT);  // wait for a bit to decide if user has stopped moving the globe

        var zoom = d3.zoom().on("start", moveStart).on("zoom", move).on("end", moveEnd);
        var drag = d3.drag().on("start", moveStart).on("drag", move).on("end", moveEnd);

        var display = d3.select("#display");

        display.call(zoom);
        display.call(drag);

        // d3.select("#show-location").on("click", function () {
        //     if (navigator.geolocation) {
        //         report.status("Finding current position...");
        //         navigator.geolocation.getCurrentPosition(function (pos) {
        //             report.status("");
        //             var coord = [pos.coords.longitude, pos.coords.latitude], rotate = globe.locate(coord);
        //             if (rotate) {
        //                 globe.projection.rotate(rotate);
        //                 configuration.save({orientation: globe.orientation()});  // triggers reorientation
        //             }
        //             dispatch.trigger("click", globe.projection(coord), coord);
        //         }, log.error);
        //     }
        // });

        function reorient() {
            var options = arguments[3] || {};
            if (!globe || options.source === "moveEnd") {
                // reorientation occurred because the user just finished a move operation, so globe is already
                // oriented correctly.
                return;
            }
            dispatch.trigger("moveStart");
            globe.orientation(configuration.get("orientation"), view);
            zoom.transform(display, d3.zoomIdentity.scale(globe.projection.scale()));
            dispatch.trigger("moveEnd");
        }

        var dispatch = _.extend({
            globe: function (_) {
                if (_) {
                    globe = _;
                    zoom.scaleExtent(globe.scaleExtent());
                    reorient();
                }
                return _ ? this : globe;
            },
            op: function () {
                moveStart();
                return op;
            },
        }, Backbone.Events);
        return dispatch.listenTo(configuration, "change:orientation", reorient);
    }

    /**
     * @param resource the GeoJSON resource's URL
     * @returns {Object} a promise for GeoJSON topology features: {boundaryLo:, boundaryHi:}
     */
    function buildMesh(resource) {
        var cancel = this.cancel;
        report.status("Downloading...");
        return µ.loadJson(resource).then(function (topo) {
            if (cancel.requested) return null;
            log.time("building meshes");
            var o = topo.objects;
            var coastLo = topojson.feature(topo, µ.isMobile() ? o.coastline_tiny : o.coastline_110m);
            var coastHi = topojson.feature(topo, µ.isMobile() ? o.coastline_110m : o.coastline_50m);
            var lakesLo = topojson.feature(topo, µ.isMobile() ? o.lakes_tiny : o.lakes_110m);
            var lakesHi = topojson.feature(topo, µ.isMobile() ? o.lakes_110m : o.lakes_50m);
            log.timeEnd("building meshes");
            return {
                coastLo: coastLo,
                coastHi: coastHi,
                lakesLo: lakesLo,
                lakesHi: lakesHi
            };
        });
    }

    /**
     * @param resource the image resource's URL
     * @returns {Object} a promise for Whether to load an image , finish load, resolve true
     */
    function loadEarthImage(resource) {
        return new Promise((resolve, reject) => {
            if (earthImage === null) {
                var image = new Image();
                image.src = resource;
                image.onload = () => {
                    earthImage = image;
                    resolve()
                };
                image.onerror = reject;
            } else resolve();
        });
    }

    /**
     * @param {String} projectionName the desired projection's name.
     * @returns {Object} a promise for a globe object.
     */
    function buildGlobe(projectionName) {
        var builder = globes.get(projectionName);
        if (!builder) {
            return when.reject("Unknown projection: " + projectionName);
        }
        return when(builder(view));
    }

    // Some hacky stuff to ensure only one download can be in progress at a time.
    var downloadsInProgress = 0;

    function buildGrids() {
        report.status("Downloading...");
        log.time("build grids");
        // UNDONE: upon failure to load a product, the unloaded product should still be stored in the agent.
        //         this allows us to use the product for navigation and other state.
        var cancel = this.cancel;
        downloadsInProgress++;
        var loaded = when.map(products.productsFor(configuration.attributes), function (product) {
            return product.load(cancel);
        });
        return when.all(loaded).then(function (products) {
            log.timeEnd("build grids");
            return {primaryGrid: products[0], overlayGrid: products[1] || products[0]};
        }).ensure(function () {
            downloadsInProgress--;
        });
    }

    /**
     * Modifies the configuration to navigate to the chronologically next or previous data layer.
     */
    function navigate(step) {
        if (downloadsInProgress > 0) {
            log.debug("Download in progress--ignoring nav request.");
            return;
        }
        let next = gridAgent.value().primaryGrid.navigate(step, configuration.get("scenario"));
        next.setUTCHours(0, 0, 0, 0);
        if (next) {
            // let startDate = new Date(products.scenarioPeriod(configuration.get("scenario")).startDate);
            // startDate.setUTCHours(next.getUTCHours(), 0, 0, 0);
            // let endDate = new Date(products.scenarioPeriod(configuration.get("scenario")).endDate);
            // endDate.setUTCHours(next.getUTCHours(), 0, 0, 0);
            // if (next < startDate) next = startDate;
            // if (next > endDate) next = endDate;
            configuration.save(µ.dateToConfig(next));
        }
    }

    function buildRenderer(mesh, globe) {
        if (!mesh || !globe) return null;
        report.status("Rendering Globe...");
        log.time("rendering map");

        // UNDONE: better way to do the following?
        var dispatch = _.clone(Backbone.Events);
        if (rendererAgent._previous) {
            rendererAgent._previous.stopListening();
        }
        rendererAgent._previous = dispatch;

        var mapContext = d3.select("#map").node().getContext("2d");
        var foregroundContext = d3.select("#foreground").node().getContext("2d");
        var locationPointContext = d3.select("#locationpoint").node().getContext("2d");
        var path = d3.geoPath().projection(globe.projection).context(mapContext).pointRadius(7);
        var foregroundPath = d3.geoPath().projection(globe.projection).context(foregroundContext).pointRadius(7);
        var locationpointPath = d3.geoPath().projection(globe.projection).pointRadius(7);

        µ.clearCanvas(d3.select("#locationpoint").node());

        function drawLocationMark(point, coord, clicked) {
            // show the location on the map if defined
            let activeLocationEnabled = coord && _.isFinite(coord[0]) && _.isFinite(coord[1]);
            let activeLocationCrossSectionEnabled = activeLocationCrossSection
                && activeLocationCrossSection.coords && (activeLocationCrossSection.coords.length > 0);

            if (!activeLocationEnabled && !activeLocationCrossSectionEnabled) return;

            let crossSectionEabled = configuration.get("crossSectionMode");
            let areaEabled = configuration.get("areaMeanMode");

            if (crossSectionEabled) { //연직단면
                let coords = activeLocationCrossSection.coords || [];
                let points = activeLocationCrossSection.points || [];
                if (clicked && coords.length === 2) {
                    // µ.clearCanvas(d3.select("#locationpoint").node());
                    // coords = [];
                    // points = [];
                    clearLocationDetailsCrossSection(true);
                    return;
                } else if (activeLocationEnabled && coords.length < 2) {
                    let pathPoint = globe.projection(coord);
                    coords.push(coord);
                    points.push(pathPoint);
                }

                if (coords.length > 0) { //클릭한 좌표에 arc렌더링..
                    coords.forEach(
                        coord => {
                            if (checkLocationInFrontGlobe(globe.projection, coord)) {
                                let pathPoint = globe.projection(coord);
                                locationPointContext.strokeStyle = "rgba(252,255,58,1.0)";
                                locationPointContext.lineWidth = 2.5;
                                locationPointContext.beginPath();
                                locationPointContext.arc(pathPoint[0], pathPoint[1], 5, 0, Math.PI * 2, true);
                                locationPointContext.stroke();

                            }
                        }
                    ) //coords.forEach( end
                } //if (coords.length > 0) { end

                if (coords.length === 2) { //클릭한 두 점 사이를 잇는 선을 렌더링..
                    // add epsilon value to first coord to calculate path between coords composed by same coord
                    let toCoord = coords[0][0] === coords[1][0] ? [coords[1][0] - 0.002, coords[1][1]] : coords[1];
                    let pathCoords = geoInterpolate(coords[0], toCoord, 10);
                    locationpointPath = d3.geoPath().projection(globe.projection).context(locationPointContext);
                    locationPointContext.strokeStyle = "rgba(252,255,58,1.0)";
                    locationPointContext.lineWidth = 2.5;
                    locationPointContext.beginPath();
                    locationpointPath({ type: "LineString", coordinates: pathCoords });
                    locationPointContext.stroke();

                    // showLocationDetailsCrossSection();
                }

                activeLocationCrossSection = { points: points, coords: coords };
            } else if (areaEabled) { //영역평균
                let coords = activeLocationCrossSection.coords || [];
                let points = activeLocationCrossSection.points || [];
                if (clicked && coords.length === 2) {
                    // return;
                    // µ.clearCanvas(d3.select("#locationpoint").node());
                    // coords = [];
                    // points = [];
                    // // clear chart
                    // µ.removeChildren(d3.select("#areaMean").node());
                    // 이미 두점을 찍은 상태에서 클릭했을때
                    clearLocationDetailsAreaMean(true);
                    return;
                } else if (activeLocationEnabled && coords.length < 2) {
                    // 아직 두점을 안찍었을때
                    let pathPoint = globe.projection(coord);
                    coords.push(coord);
                    points.push(pathPoint);
                }

                if (coords.length > 0 && coords.length < 2) {
                    // 클릭한 좌표가 있으면 캔버스에 arc를 렌더링함
                    coords.forEach(
                        coord => {
                            if (checkLocationInFrontGlobe(globe.projection, coord)) {
                                let pathPoint = globe.projection(coord);
                                locationPointContext.strokeStyle = "rgba(252,255,58,1.0)";
                                locationPointContext.lineWidth = 2.5;
                                locationPointContext.beginPath();
                                locationPointContext.arc(pathPoint[0], pathPoint[1], 5, 0, Math.PI * 2, true);
                                locationPointContext.stroke();
                            }
                        }
                    )
                }
                if (coords.length === 2) { // 두 좌표를 기준으로 범위테두리와 영역을 노란색으로 칠함 , 원리는 모르겠음
                    let toCoord = coords[0][0] === coords[1][0] ? [coords[1][0] + 0.002, coords[1][1]] : coords[1];
                    let w = coords[0][0], s = toCoord[1], e = toCoord[0], n = coords[0][1];
                    // if(w * e < 0){
                    // 	if(w < 0){
                    // 		// w = (w < 0) ? 360 + w : w;
                    // 		// e = (e < 0) ? 360 + e : e;
                    // 	} else {
                    // 		w = (w < 0) ? 360 + w : w;
                    // 		e = (e < 0) ? 360 + e : e;
                    // 	}
                    // }

                    let arc = d3.geoGraticule().extentMajor([[w, s], [e, n]]);

                    µ.clearCanvas(d3.select("#locationpoint").node());
                    locationpointPath = d3.geoPath().projection(globe.projection).context(locationPointContext);
                    locationPointContext.strokeStyle = "rgba(252,255,58,1.0)";
                    locationPointContext.fillStyle = "rgba(252,255,58,0.2)";
                    locationPointContext.lineWidth = 2.5;
                    locationPointContext.beginPath();
                    locationpointPath(arc.outline());
                    locationPointContext.stroke();
                    locationPointContext.fill();

                    // showLocationDetailsAreaMean();

                }
                activeLocationCrossSection = { points: points, coords: coords };
            } else {
                activeLocation = {point: point, coord: coord};
                drawBasicLocationPoint();
                // µ.clearCanvas(d3.select("#locationpoint").node());
                // let pathPoint = globe.projection(coord);
                // const currentValue = getCurrentValue(coord);
                // if (checkLocationInFrontGlobe(globe.projection, coord)) {
                //     locationPointContext.strokeStyle = "rgba(0,255,0,1.0)";
                //     locationPointContext.lineWidth = 2.5;
                //     locationPointContext.beginPath();
                //     locationPointContext.arc(pathPoint[0], pathPoint[1], 5, 0, Math.PI * 2, true);
                //     locationPointContext.stroke();
                //     if (currentValue.value) { // 기본모드는 클릭한 지점에 현재 값을 보여줘야함
                //         locationPointContext.font = "16pt Fira";
                //         locationPointContext.strokeStyle = "white";
                //         locationPointContext.lineWidth = 0.8;
                //         locationPointContext.strokeText(currentValue.value.toFixed(2), pathPoint[0], pathPoint[1])
                //
                //         locationPointContext.fillStyle = 'rgba(' + currentValue.gradient[0] + "," + currentValue.gradient[1] + "," + currentValue.gradient[2] + "," + currentValue.gradient[3] + ')'
                //         locationPointContext.fillText(currentValue.value.toFixed(2), pathPoint[0], pathPoint[1])
                //     }
                // }
            }
        }

        function geoInterpolate(start, end, step) {
            let coords = [];
            let midX = (end[0] + start[0]) / 2;
            midX = midX < start[0] ? midX < 0 ? (midX + 180) : (midX - 180) : midX;
            let mid = [midX, start[1] + (end[1] - start[1]) / 2];
            step--;
            if (step <= 0) return coords;

            coords.push(start);
            geoInterpolate(start, mid, step).forEach(d => coords.push(d));
            geoInterpolate(mid, end, step).forEach(d => coords.push(d));
            coords.push(end);

            return coords.slice(0);
            // return coords.slice(0).map(d => [parseFloat(d[0]).toFixed(3), parseFloat(d[1]).toFixed(3)])
            // 					  .filter((item, i, array) => array.findIndex((item2, j) => item[0] === item2[0] && item[1] === item2[1]));
        }

        //click했을때의 draw와 zoom,drag했을때의 draw를 구분하기 위해 클릭용 함수를 씌웠음
        function drawLocationMarkClicked(point, coord) {
            // show the location on the map if defined
            if (fieldAgent.value() && !fieldAgent.value().isInsideBoundary(point[0], point[1])) {
                // UNDONE: Sometimes this is invoked on an old, released field, because new one has not been
                //         built yet, causing the mark to not get drawn.
                return;  // outside the field boundary, so ignore.
            }
            drawLocationMark(point, coord, true);
        }

        // Draw the location mark if one is currently visible.
        // if (activeLocation.point && activeLocation.coord !== [] && activeLocation.coord !== [NaN, NaN]) {
        //     drawLocationMark(activeLocation.point, activeLocation.coord);
        // }

        // Throttled draw method helps with slow devices that would get overwhelmed by too many redraw events.
        var REDRAW_WAIT = 5;  // milliseconds
        var doDraw_throttled = _.throttle(doDraw, REDRAW_WAIT, {leading: false, trailing: false});
        var drawLocationMarkClicked_throttled = _.debounce(drawLocationMarkClicked, REDRAW_WAIT);

        function drawMap(meshHi) {
            µ.clearCanvas(d3.select("#map").node());
            globe.drawMap(mapContext, path,
                {"coast": meshHi ? mesh.coastHi : mesh.coastLo, "lakes": meshHi ? mesh.lakesHi : mesh.lakesLo});
            globe.drawGraticuleHemisphere(mapContext, path);

            µ.clearCanvas(d3.select("#foreground").node());
            globe.drawForeground(foregroundContext, foregroundPath);

            //zoon/drag시 포인트를 다시 리렌더링, click이벤트로 일어나는것이 아니다
            µ.clearCanvas(d3.select("#locationpoint").node());
            drawLocationMark(activeLocation.point, activeLocation.coord);
            // d3.select("#locationpoint").selectAll("path").attr("d", locationpointPath);

            globe.drawWing(foregroundContext, globe.projection, globe.projection.scale(), globe.projection.center);
        }

        function doDraw() {
            drawMap(false);
            rendererAgent.trigger("redraw");
        }

        // sainy !! move zoom rendering
        // Attach to map rendering events on input controller.
        dispatch.listenTo(
            inputController, {
                moveStart: function () {
                    drawMap(true);
                    rendererAgent.trigger("start");
                },
                move: function () {
                    doDraw();
                    // doDraw_throttled();
                },
                moveEnd: function () {
                    drawMap(true);
                    rendererAgent.trigger("render");
                },
                click: function (point, coord) {
                    drawLocationMarkClicked_throttled(point, coord);
                }
            });

        // Finally, inject the globe model into the input controller. Do it on the next event turn to ensure
        // renderer is fully set up before events start flowing.
        when(true).then(function () {
            inputController.globe(globe);
        });

        log.timeEnd("rendering map");
        return "ready";
    }

    function createMask(globe) {
        if (!globe) return null;

        log.time("render mask");
        // Create a detached canvas, ask the model to define the mask polygon, then fill with an opaque color.
        var width = view.width, height = view.height;
        var canvas = d3.select(document.createElement("canvas")).attr("width", width).attr("height", height).node();
        var context = globe.defineMask(canvas.getContext("2d"));
        context.fillStyle = "rgba(0, 0, 0, 1)";
        context.fill();
        // d3.select("#display").node().appendChild(canvas);  // make mask visible for debugging

        var imageData = context.getImageData(0, 0, width, height);
        var data = imageData.data;  // layout: [r, g, b, a, r, g, b, a, ...]
        log.timeEnd("render mask");
        return {
            imageData: imageData,
            isVisible: function (x, y) {
                var i = (y * width + x) * 4;
                return data[i + 3] > 0;  // non-zero alpha means pixel is visible
            },
            set: function (x, y, rgba) {
                var i = (y * width + x) * 4;
                data[i] = rgba[0];
                data[i + 1] = rgba[1];
                data[i + 2] = rgba[2];
                data[i + 3] = rgba[3];
                return this;
            }
        };
    }

    function createField(columns, bounds, mask) {
        /**
         * @returns {Array} wind vector [u, v, magnitude] at the point (x, y), or [NaN, NaN, null] if wind
         *          is undefined at that point.
         */
        function field(x, y) {
            var column = columns[Math.round(x)];
            return column && column[Math.round(y)] || NULL_WIND_VECTOR;
        }

        /**
         * @returns {boolean} true if the field is valid at the point (x, y)
         */
        field.isDefined = function (x, y) {
            return field(x, y)[2] !== null;
        };

        /**
         * @returns {boolean} true if the point (x, y) lies inside the outer boundary of the vector field, even if
         *          the vector field has a hole (is undefined) at that point, such as at an island in a field of
         *          ocean currents.
         */
        field.isInsideBoundary = function (x, y) {
            return field(x, y) !== NULL_WIND_VECTOR;
        };

        // Frees the massive "columns" array for GC. Without this, the array is leaked (in Chrome) each time a new
        // field is interpolated because the field closure's context is leaked, for reasons that defy explanation.
        field.release = function () {
            columns = [];
        };

        field.randomize = function (o) {  // UNDONE: this method is terrible
            var x, y;
            var safetyNet = 0;
            do {
                x = Math.round(_.random(bounds.x, bounds.xMax));
                y = Math.round(_.random(bounds.y, bounds.yMax));
            } while (!field.isDefined(x, y) && safetyNet++ < 30);
            o.x = x;
            o.y = y;
            return o;
        };

        field.overlay = mask.imageData;

        return field;
    }

    /**
     * Calculate distortion of the wind vector caused by the shape of the projection at point (x, y). The wind
     * vector is modified in place and returned by this function.
     */
    function distort(projection, λ, φ, x, y, scale, wind) {
        var u = wind[0] * scale;
        var v = wind[1] * scale;
        var d = µ.distortion(projection, λ, φ, x, y);

        // Scale distortion vectors by u and v, then add.
        wind[0] = d[0] * u + d[2] * v;
        wind[1] = d[1] * u + d[3] * v;
        return wind;
    }

    function interpolateField(globe, grids) {
        if (!globe || !grids || !rendererAgent.value()) return null;

        var quickoverlay = quickoverlayAgent.value();
        var availableQuickoverlay = quickoverlay && quickoverlay.render(globe.quickoverlay, grids);
        var primaryGrid = grids.primaryGrid;
        var overlayGrid = grids.overlayGrid;

        log.time("interpolating field");
        var d = when.defer(), cancel = this.cancel;

        var projection = globe.projection;
        var bounds = globe.bounds(view);
        var velocityScale = bounds.height * primaryGrid.particles.velocityScale;
        var mask = createMask(globe, projection);

        var columns = [];
        var point = [];
        var x = bounds.x;
        var interpolate = primaryGrid.interpolate;
        var overlayInterpolate = overlayGrid.interpolate;
        var hasDistinctOverlay = primaryGrid !== overlayGrid;
        let header = overlayGrid.header;
        var scale = overlayGrid.scale;

        function interpolateColumn(x) {
            var column = [];
            for (var y = bounds.y; y <= bounds.yMax; y += 2) {
                if (mask.isVisible(x, y)) {
                    point[0] = x;
                    point[1] = y;
                    var coord = projection.invert(point);
                    var color = TRANSPARENT_BLACK;
                    var wind = NULL_WIND_VECTOR;
                    if (coord) {
                        var λ = coord[0], φ = coord[1];
                        if (isFinite(λ)) {
                            var scalar = null;
                            wind = interpolate(λ, φ);
                            if (wind) {
                                wind = distort(projection, λ, φ, x, y, velocityScale, wind);
                                scalar = wind[2];
                            }
                            if (!availableQuickoverlay) {
                                if (hasDistinctOverlay) {
                                    scalar = overlayInterpolate(λ, φ);
                                }
                                if (µ.isValue(scalar)) {
                                    if (header.parameterNumberName === "Od550aer") {
                                        //미세먼지는 수치에 따라 투명도 가중치를 적용한다.
                                        let ratio = 1;
                                        if (scalar < 0.3) ratio = 0.5;
                                        else ratio = 2;

                                        const alpah = scalar * ratio * OVERLAY_ALPHA > 210 ? 210 : scalar * 1.5 * OVERLAY_ALPHA;
                                        if (scalar === null) color = TRANSPARENT_BLACK;
                                        else color = scale.gradient(scalar, alpah);
                                    } else {
                                        color = scale.gradient(scalar, OVERLAY_ALPHA);
                                    }
                                    if (isNaN(color[0])) {
                                        color = [250, 250, 250, OVERLAY_ALPHA * 0.5];
                                    }
                                    if (scale.customBounds.length > 0 && (scale.customBounds[0] !== "" || scale.customBounds[1] !== "")) {
                                        if (scale.customBounds[0] !== "" && scale.customBounds[1] !== "") {
                                            if (scalar < scale.customBounds[0] || scalar > scale.customBounds[1]) {
                                                color = TRANSPARENT_BLACK;
                                            }
                                        } else if (scale.customBounds[0] !== "") {
                                            if (scalar < scale.customBounds[0]) {
                                                color = TRANSPARENT_BLACK;
                                            }
                                        } else {
                                            if (scalar > scale.customBounds[1]) {
                                                color = TRANSPARENT_BLACK;
                                            }
                                        }
                                    } // if (scale.customBounds.length > 0 && (scale.customBounds[0] !== "" || scale.customBounds[1] !== "")) { end
                                } //if (µ.isValue(scalar)) { end
                            } //if (!availableQuickoverlay) { end
                        } //if (isFinite(λ)) { end
                    } //if (coord) { end
                    column[y + 1] = column[y] = wind || HOLE_VECTOR;
                    if (!availableQuickoverlay) {
                        mask.set(x, y, color).set(x + 1, y, color).set(x, y + 1, color).set(x + 1, y + 1, color);
                    }// x,y가 2씩 늘어나니까..ㅋㅋ
                }
            }
            columns[x + 1] = columns[x] = column;
        }

        report.status("");

        if (!globe.quickoverlay) {
            report.progress(0);
        }

        // log.time("interpolating column");
        (function batchInterpolate() {
            try {
                if (!cancel.requested) {
                    var start = Date.now();
                    while (x < bounds.xMax) {
                        interpolateColumn(x);
                        x += 2;
                        if ((Date.now() - start) > MAX_TASK_TIME) {
                            // Interpolation is taking too long. Schedule the next batch for later and yield.
                            if (!globe.quickoverlay) {
                                report.progress((x - bounds.x) / (bounds.xMax - bounds.x));
                            }
                            setTimeout(batchInterpolate, MIN_SLEEP_TIME);
                            return;
                        }
                    }
                }
                // log.timeEnd("interpolating column");
                d.resolve(createField(columns, bounds, mask));
            } catch (e) {
                d.reject(e);
            }
            if (!globe.quickoverlay) {
                report.progress(1);  // 100% complete
            }
            log.timeEnd("interpolating field");
        })();

        return d.promise;
    }

    function animate(globe, field, grids) {
        if (!globe || !field || !grids) return;
        var cancel = this.cancel;
        var bounds = globe.bounds(view);

        // maxIntensity is the velocity at which particle color intensity is maximum
        var colorStyles = µ.windIntensityColorScale(INTENSITY_SCALE_STEP, grids.primaryGrid.particles.maxIntensity);
        var buckets = colorStyles.map(function () {
            return [];
        });
        var particleCount = Math.round(bounds.width * PARTICLE_MULTIPLIER);
        if (µ.isMobile()) {
            particleCount *= PARTICLE_REDUCTION;
        }
        // particleCount = 100;
        var fadeFillStyle = µ.isFF() ? "rgba(0, 0, 0, 0.95)" : "rgba(0, 0, 0, 0.97)";  // FF Mac alpha behaves oddly

        // log.debug("particle count: " + particleCount);
        var particles = [];
        for (var i = 0; i < particleCount; i++) {
            particles.push(field.randomize({age: _.random(0, MAX_PARTICLE_AGE)}));
        }

        let weight = grids.primaryGrid.type === 'currents' ? 2.5 : 1;

        function evolve() {
            buckets.forEach(function (bucket) {
                bucket.length = 0;
            });
            particles.forEach(function (particle) {
                if (particle.age > MAX_PARTICLE_AGE) {
                    field.randomize(particle).age = 0;
                }
                var x = particle.x;
                var y = particle.y;

                var v = field(x, y);  // vector at current position
                var m = v[2];
                if (m === null) {
                    particle.age = MAX_PARTICLE_AGE;  // particle has escaped the grid, never to return...
                } else {
                    var xt = x + v[0] * weight;
                    var yt = y + v[1] * weight;
                    if (field.isDefined(xt, yt)) {
                        // Path from (x,y) to (xt,yt) is visible, so add this particle to the appropriate draw bucket.
                        particle.xt = xt;
                        particle.yt = yt;
                        buckets[colorStyles.indexFor(m)].push(particle);
                    } else {
                        // Particle isn't visible, but it still moves through the field.
                        particle.x = xt;
                        particle.y = yt;
                    }
                }
                particle.age += 1;
            });
        }

        var g = d3.select("#animation").node().getContext("2d");
        g.lineWidth = PARTICLE_LINE_WIDTH;
        g.fillStyle = fadeFillStyle;

        function draw() {
            // Fade existing particle trails.
            var prev = g.globalCompositeOperation;
            g.globalCompositeOperation = "destination-in";
            g.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
            g.globalCompositeOperation = prev;

            // Draw new particle trails.
            buckets.forEach(function (bucket, i) {
                if (bucket.length > 0) {
                    g.beginPath();
                    g.strokeStyle = colorStyles[i];
                    bucket.forEach(function (particle) {
                        g.moveTo(particle.x, particle.y);
                        g.lineTo(particle.xt, particle.yt);
                        particle.x = particle.xt;
                        particle.y = particle.yt;
                    });
                    g.stroke();
                }
            });
        }

        (function frame() {
            try {
                if (cancel.requested) {
                    field.release();
                    return;
                }
                evolve();
                draw();
                setTimeout(frame, FRAME_RATE);
            } catch (e) {
                report.error(e);
            }
        })();
    }

    function drawGridPoints(ctx, grid, globe) {
        if (!grid || !globe || !configuration.get("showGridPoints")) return;

        ctx.fillStyle = "rgba(255, 255, 255, 1)";
        // Use the clipping behavior of a projection stream to quickly draw visible points.
        var stream = globe.projection.stream({
            point: function (x, y) {
                ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
            }
        });
        grid.forEachPoint(function (λ, φ, d) {
            if (µ.isValue(d)) {
                stream.point(λ, φ);
            }
        });
    }

    function drawContour(showContourLabel) {
        var grid = (gridAgent.value() || {}).overlayGrid;
        var currentGlobe = inputController.globe;
        var currentScale = grid.scale;

        var units = createUnitToggle(grid.type === "wind" ? "#location-info-wind-units" : "#location-info-overlay-units", grid).value();

        if (!currentGlobe()) return;

        var ctx = d3.select("#contour").node().getContext("2d");
        µ.clearCanvas(d3.select("#contour").node());
        µ.clearCanvas(d3.select("#contourLabel").node());
        var header = grid.header;

        var λ0 = header.lo1, φ0 = header.la1;  // the grid's origin (e.g., 0.0E, 90.0N)
        var Δλ = header.dx, Δφ = header.dy;    // distance between grid points (e.g., 2.5 deg lon, 2.5 deg lat)
        var ni = header.nx, njj = header.ny;    // number of grid points W-E and N-S (e.g., 144 x 73)
        var offsetLo = Δλ / 2 - λ0;
        var lo2 = 360;


        let rangeStart = grid.scale.bounds[0];
        let rangeStop = grid.scale.bounds[1];
        let step = configuration.get("step");
        step = step && _.isFinite(step) && step > 0.0001 ? step : 3;
        var epsilon = 1e-5;
        var loRotate = 180, loStart = -180, loStop = 180,
            loStartOffset = loStart + epsilon, loStopOffset = loStop - epsilon;

        //21.06.30 by shson, 해빙의 경우 0값에 대한 contour을 표출하면 이상한 contour이 생겨서 0값에 대한 contour을 제거하기위해 rangeStart를 1로 바꿔줌
        if (configuration.get("overlayType") === "sea_ice_area" && rangeStart === 0) rangeStart += 1;

        var contours = d3.contours()
            .size([ni + 1, njj])
            .smooth(true)
            .thresholds(d3.range(rangeStart, rangeStop, step));

        var currentProjection = currentGlobe().projection;
        var contourGenerator = d3.geoPath()
            .projection(currentProjection)
            .context(ctx);

        var overlayGridPoints = [];

        grid.forEachPoint(function (λ, φ, d) {
            if (µ.isValue(d)) {
                overlayGridPoints.push(d);
            } else { //invalid data, 실제로 등치선이 그려지지 않게 하기위해 범위 밖의 값을 임의로 넣음
                overlayGridPoints.push(-1000);
            }
        });
        overlayGridPoints = rotate(overlayGridPoints);
        overlayGridPoints = cylinder(overlayGridPoints);

        // convert 0~360 to -180~180
        function rotate(values) {
            var v = new Float32Array(njj * ni);
            for (var j = 0; j < njj; j++) {
                for (var i = 0; i < ni; i++) {
                    var value = values[j * ni + i];
                    if (value.length === 2) { // velocity
                        v[j * ni + i] = Math.sqrt(value[0] * value[0] + value[1] * value[1]);
                    } else {
                        v[j * ni + i] = value;
                    }
                }
            }

            var l = ni >> 1;
            for (var j = 0, k = 0; j < njj; ++j, k += ni) {
                // v.subarray(k, k + l).reverse();
                // v.subarray(k + l, k + ni).reverse();
                // v.subarray(k, k + ni).reverse();
                v = reverseArr(v.subarray(k, k + l), v, k, k + l);
                v = reverseArr(v.subarray(k + l, k + ni), v, k + l, k + ni);
                v = reverseArr(v.subarray(k, k + ni), v, k, k + ni);
            }
            return v;
        }

        // reverse array not available on IE
        function reverseArr(input, source, start, end) {
            var v = new Float32Array(input.length);

            for (var i = input.length - 1, j = 0; i >= 0; i--) {
                v[j++] = input[i];
            }

            for (var i = 0, k = 0; i < source.length; i++) {
                if (i >= start && i <= (end - 1)) {
                    source[i] = v[k++];
                }
                if (i > (end - 1)) {
                    break;
                }
            }

            return source;
        }


        // duplicate left column to the right for stitch
        function cylinder(values) {
            var v = new Float32Array(njj * (ni + 1));
            for (var j = 0; j < njj; j++) {
                for (var i = 0; i < ni; i++) {
                    v[j * (ni + 1) + i] = values[j * ni + i];
                }
                v[j * (ni + 1) + ni] = values[j * ni];
            }
            return v;
        }

        contours(overlayGridPoints)
            .map(function (d) { // invert x/y pixel points to lon/lan coords
                var shared = {};
                var p = {
                    type: "Polygon",
                    coordinates: d3.merge(d.coordinates.map(function (polygon) {
                        return polygon.map(function (ring) {
                            return ring.map(function (point) {
                                var coordX = point[0] / (ni + 1) * lo2 - loRotate;
                                return [point[0] / (ni + 1) * lo2 - loRotate, φ0 - point[1] / njj * 180];
                            }).reverse();
                        });
                    }))
                };

                // // Add contact points with the antimeridian.
                p.coordinates = p.coordinates
                    .map(function (ring) {
                        ring.pop();
                        var r = [];
                        for (var i = 0, l = ring.length; i < l; i++) {
                            var p = ring[i];
                            if (p[0] === loStop && ring[(i + l - 1) % l][0] < loStop)
                                r.push([p[0], p[1] + epsilon]);
                            if (p[0] === loStart && ring[(i + l - 1) % l][0] > loStart)
                                r.push([p[0], p[1] - epsilon]);
                            r.push(p);
                            if (p[0] === loStop && ring[(i + 1) % l][0] < loStop)
                                r.push([p[0], p[1] - epsilon]);
                            if (p[0] === loStart && ring[(i + 1) % l][0] > loStart)
                                r.push([p[0], p[1] + epsilon]);
                        }
                        r.push(r[0]);
                        return r;
                    });

                // Record the y-intersections with the antimeridian.
                p.coordinates.forEach(function (ring) {
                    var labeled = false;
                    ring.forEach(function (p) {
                        if (p[0] === loStart || p[0] === loStop) {
                            shared[p[1]] |= p[0] === loStart ? 1 : 2;
                        }
                    });
                });

                // Offset any unshared antimeridian points to prevent their stitching.
                p.coordinates.forEach(function (ring) {
                    ring.forEach(function (p) {
                        if ((p[0] === loStart || p[0] === loStop) && shared[p[1]] !== 3) {
                            p[0] = p[0] === loStart ? loStartOffset : loStopOffset;
                        }
                    });
                });

                p = geoStitch(p, loStart, loStop);

                // After stitching remove the shared points -- they are off
                // see https://github.com/d3/d3-contour/issues/25
                p.coordinates = p.coordinates
                    .map(function (ring) {
                        ring.pop();
                        ring = ring
                            .map(function (p) {
                                var p0 = (p[0] + loStop) * (ni + 1) / (ni) + loStart - offsetLo; // add offsetLo to adop lo1 coord value
                                return [p0, p[1]];
                            });
                        ring = ring.filter(function (p) {
                            return Math.abs(p[0]) < loStopOffset;
                        });
                        if (ring[0]) {
                            ring.push(ring[0]);
                        }
                        return ring;
                    })
                    .filter(function (ring) {
                        return ring.length > 0;
                    });

                // If the MultiPolygon is empty, treat it as the Sphere.
                return p.coordinates.length
                    ? {type: "Polygon", coordinates: p.coordinates, value: d.value}
                    : {type: "Sphere", value: d.value};

            })
            .forEach(function (geometry, i) { // draw geometry coords to path (projection, canvas context)
                ctx.beginPath();
                contourGenerator(geometry);
                ctx.closePath();
                ctx.lineJoin = "round";
                ctx.lineWidth = µ.isMobile() ? 1.4 : 0.7;
                var gradient = currentScale.gradient(geometry.value, OVERLAY_ALPHA);
                var strokeStyle = "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")"; //"rgba(170,240,250,1.0)";
                ctx.strokeStyle = strokeStyle;
                ctx.stroke();
                if (showContourLabel) {
                    fillLabel(currentProjection, geometry, strokeStyle);
                }
            });

        function fillLabel(currentProjection, geometry, strokeStyle) {
            var ctx = d3.select("#contourLabel").node().getContext("2d");

            for (var i = 0; geometry.type === "Polygon" && i < geometry.coordinates.length; i++) {
                var offSet = (i % geometry.coordinates.length) * 5; // to pervent the same vertical line

                var centerCoord = currentProjection.invert([view.width / 2, view.height / 2]);
                var sourceCoord = centerProximateCoord(geometry.coordinates[i], centerCoord, offSet);
                var distance = d3.geoDistance(sourceCoord, centerCoord);

                if (configuration.get("projection") === "orthographic" && distance > 1.34) {
                    continue;
                }

                var pos = currentProjection(sourceCoord);

                // var degree = Math.atan(pos.tangentY/pos.tangentX);
                var text = µ.formatScalar(geometry.value, units);

                ctx.save();

                ctx.translate(pos[0], pos[1]);
                // ctx.rotate(degree);

                ctx.font = µ.isMobile() ? "18px Arial" : "9px Arial";
                ctx.strokeStyle = strokeStyle; //"rgba(170,240,250,1.0)";
                ctx.fillStyle = strokeStyle; //"rgba(170,240,250,1.0)";
                if (µ.isMobile()) roundRect(ctx, -4 - ctx.measureText(text).width / 2, -16, 8 + ctx.measureText(text).width, 18, 8, true, true);
                else roundRect(ctx, -2 - ctx.measureText(text).width / 2, -8, 4 + ctx.measureText(text).width, 9, 4, true, true);
                ctx.fillStyle = "rgba(0,0,0,1.0)";
                ctx.fillText(text, -ctx.measureText(text).width / 2, 0.3);
                ctx.restore();
            }
        }

        function centerProximateCoord(coordinates, centerCoord, offSet) {
            var sourceCoord = coordinates.pop(),
                prevDistance = 360,
                distance;

            coordinates.forEach(function (coord) {
                var x = coord[0] + offSet;
                distance = Math.abs((x < 0 ? (360 + x) : x) - (centerCoord[0] < 0 ? (360 + centerCoord[0]) : centerCoord[0]));
                distance = distance > 180 ? 360 - distance : distance;
                if (distance < prevDistance) {
                    sourceCoord = coord;
                    prevDistance = distance;
                }
            });

            return sourceCoord;
        }
    }

    function roundRect(ctx, x, y, width, height, radius, fill, stroke) {
        if (typeof stroke === "undefined") {
            stroke = true;
        }
        if (typeof radius === "undefined") {
            radius = 5;
        }
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        if (stroke) {
            ctx.stroke();
        }
        if (fill) {
            ctx.fill();
        }
    }

    // function roundRectR(ctx, x, y, width, height, radius, fill, stroke) {
    //     if (typeof stroke === "undefined") {
    //         stroke = true;
    //     }
    //     if (typeof radius === "undefined") {
    //         radius = 5;
    //     }
    //     ctx.beginPath();
    //     ctx.moveTo(x + radius, y);
    //     ctx.lineTo(x + width - radius, y);
    //     ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    //     ctx.lineTo(x + width, y + height - radius);
    //     ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    //     ctx.lineTo(x + radius, y + height);
    //     ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    //     ctx.lineTo(x, y + radius);
    //     ctx.quadraticCurveTo(x, y, x + radius, y);
    //     ctx.closePath();
    //     if (stroke) {
    //         ctx.stroke();
    //     }
    //     if (fill) {
    //         ctx.fill();
    //     }
    // }

    function drawWindVector() {
        var grid = (gridAgent.value() || {}).primaryGrid;
        var scale = grid.scale;
        var currentGlobe = inputController.globe;
        var field = fieldAgent.value();

        if (!grid || !currentGlobe() || !configuration.get("showWindVector")) return;

        var header = grid.header;

        var ni = header.nx, njj = header.ny;    // number of grid points W-E and N-S (e.g., 144 x 73)
        var rangeStart = grid.scale.bounds[0],
            rangeStop = grid.scale.bounds[1],
            minRange = rangeStop < 2 ? 0.0 : rangeStart,
            maxRange = rangeStop < 2 ? 4.0 : 8.6;

        var arrowSize = rangeStop < 2 ? 22 : 25;
        var maxSpeed = 0;

        var currentProjection = currentGlobe().projection;
        var ctx = d3.select("#windVector").node().getContext("2d");

        µ.clearCanvas(d3.select("#windVector").node());

        grid.forEachPoint(function (λ, φ, d) {
            var wind = Math.sqrt(λ * λ + φ * φ).toFixed(2);
            if (µ.isValue(wind)) {
                if (maxSpeed < wind) {
                    maxSpeed = wind;
                }
            }
        });

        var sizeScale = d3.scaleLinear().domain([rangeStart, rangeStop]).range([minRange, maxRange]);
        var centerCoord = currentProjection.invert([view.width / 2, view.height / 2]);
        var i = 0, j = 0;
        var windVectorRate = 2300 / 300;  // when current scale is about 300, wind vector counts are about 2300
        var conversion = Math.sqrt(300 / currentProjection.scale()) * Math.sqrt(ni / 192); // variable for scale rate by map and mode 
        var xRate = parseInt(windVectorRate / 2.53 * conversion) === 0 ? 1 : parseInt(windVectorRate / 2.53 * conversion),
            yRate = parseInt(windVectorRate / 1.89 * conversion) === 0 ? 1 : parseInt(windVectorRate / 1.89 * conversion);

        grid.forEachPoint(function (λ, φ, d) {
            var wind = Math.sqrt(d[0] * d[0] + d[1] * d[1]).toFixed(2);
            if (µ.isValue(wind) && (parseInt(i % xRate) === 0) && ((parseInt(i / ni) % yRate) === 0)) {
                var distance = d3.geoDistance([λ, φ], centerCoord);

                if (!(configuration.get("projection") === "orthographic" && distance > 1.57)) { // except for wind behind the earth
                    drawVectorArrow(ctx, currentProjection, λ, φ, d, wind, arrowSize, true, sizeScale);
                    j++;
                }
            }
            i++;
        });
    }

    function drawVectorArrow(ctx, projection, λ, φ, d, wind, arrowSize, scaleable, sizeScale) {
        var pos = projection([λ, φ]);
        var angle = Math.atan2(-d[1], d[0]);

        ctx.save();
        ctx.translate(pos[0], pos[1]);
        ctx.rotate(angle);
        if (scaleable) {
            ctx.scale(sizeScale(wind), sizeScale(wind));
        }

        ctx.beginPath()
        ctx.strokeStyle = "rgba(255,255,255,0.8)";
        ctx.moveTo(-arrowSize / 2, 0);
        ctx.lineTo(arrowSize / 2, 0);
        ctx.lineTo(arrowSize / 5, arrowSize / 6);
        ctx.moveTo(arrowSize / 2, 0);
        ctx.lineTo(arrowSize / 5, -arrowSize / 6);
        ctx.stroke();
        ctx.closePath();

        ctx.restore();
    }

    function drawImageOverlay(globe) {
        if (globe.quickoverlay) {
            return;
        }
        var ctx = d3.select("#earthImage").node().getContext("2d");
        const canvasWidth = ctx.canvas.width;
        const canvasHeight = ctx.canvas.height;
        var projection = globe.projection;
        var bounds = globe.bounds(view);
        var mask = createMask(globe, projection);
        var point = [];
        var tmpCtx = d3.select(document.createElement("canvas")).attr("width", canvasWidth).attr("height", canvasHeight).node().getContext("2d");
        tmpCtx.drawImage(earthImage, bounds.x, bounds.y, bounds.width, bounds.height);

        var sourceData = tmpCtx.getImageData(bounds.x, bounds.y, bounds.width, bounds.height).data;
        for (var y = bounds.y; y <= bounds.yMax; y += 1) {
            for (var x = bounds.x; x <= bounds.xMax; x += 1) {
                if (mask.isVisible(x, y)) {
                    point[0] = x;
                    point[1] = y;
                    var coord = projection.invert(point);
                    if (coord === undefined || coord[0] > 180 || coord[0] < -180 || coord[1] > 90 || coord[1] < -90) {
                        continue;
                    }
                    const imageX = Math.floor((coord[0] + 180) * bounds.width / 360); //좌표에 해당하는 x,y값을 가져옴
                    const imageY = Math.floor((90 - coord[1]) * bounds.height / 180);
                    const sourceIndex = (imageY * bounds.width + imageX) * 4;
                    mask.set(x, y, [sourceData[sourceIndex] +70, sourceData[sourceIndex + 1] +70, sourceData[sourceIndex + 2] +70, sourceData[sourceIndex + 3] -100]);
                    // mask.set(x, y, [sourceData[sourceIndex], sourceData[sourceIndex + 1], sourceData[sourceIndex + 2], sourceData[sourceIndex + 3]]);
                }
            }
        }
        ctx.putImageData(mask.imageData, 0, 0);
    }

    function drawOverlay(field, overlayType, showContour, showContourLabel, showWindVector) {
        if (!field) return;
        var dispatch = _.clone(Backbone.Events);
        if (overlayAgent._previous) {
            overlayAgent._previous.stopListening();
        }
        overlayAgent._previous = dispatch;

        var globe = inputController.globe();
        var quickoverlay = quickoverlayAgent.value();
        if (quickoverlay && overlayType === "off" || quickoverlay && !globe.quickoverlay) {
            quickoverlay.clearResource();
            quickoverlay = null;
        }

        if (quickoverlay && globe.quickoverlay) {
            quickoverlay.quickRender();
        }
        var ctx = d3.select("#overlay").node().getContext("2d"), grid = (gridAgent.value() || {}).overlayGrid;

        function clearContext(context) {
            var canvas = context.canvas;
            context.clearRect(0, 0, canvas.width, canvas.height);
        }

        clearContext(ctx);
        µ.clearCanvas(d3.select("#contour").node());
        µ.clearCanvas(d3.select("#contourLabel").node());
        µ.clearCanvas(d3.select("#windVector").node());
        µ.clearCanvas(d3.select("#overlay").node());
        µ.clearCanvas(d3.select("#scale").node());
        µ.clearCanvas(d3.select("#earthImage").node());



        if (overlayType) {
            if (overlayType !== "off") {
                if (!globe.quickoverlay) {
                    ctx.putImageData(field.overlay, 0, 0);
                    drawImageOverlay(globeAgent.value());
                }
                if (showContour) {
                    drawContour(showContourLabel);
                }
                if (showWindVector) {
                    drawWindVector();
                }
            }
            drawGridPoints(ctx, grid, globeAgent.value());
        }
        if (grid) { //세로 컬러바로 변경
            // Draw color bar for reference.
            if (!µ.isMobile()) { //PC일때
                var colorBar = d3.select("#scale"), scale = grid.scale, bounds = scale.bounds;
                var c = colorBar.node(), g = c.getContext("2d"), n = c.height - 1;

                for (var i = 0; i <= n; i++) {
                    var rgb = scale.gradient(µ.spread((n - i)/ n, bounds[0], bounds[1]), 1);
                    g.fillStyle = "rgb(" + rgb[0] + "," + rgb[1] + "," + rgb[2] + ")";

                    if (i <= 20) {
                        g.fillRect(0 - i + 4, i, c.width + i * 2 - 8, 1);
                    } else if (i >= n - 20) {
                        g.fillRect(0 - (n - i) + 4, i, c.width + (n - i) * 2 - 8, 1);
                    } else {
                        g.fillRect(0, i, c.width, 1);
                    }
                }
            } else {
                var colorBar = d3.select("#scale"), scale = grid.scale, bounds = scale.bounds;
                var c = colorBar.node(), g = c.getContext("2d"), n = c.width - 1;

                for (var i = 0; i <= n; i++) {
                    var rgb = scale.gradient(µ.spread(i / n, bounds[0], bounds[1]), 1);
                    g.fillStyle = "rgb(" + rgb[0] + "," + rgb[1] + "," + rgb[2] + ")";

                    if (i <= 20) {
                        g.fillRect(i, 0 - i + 4, 1, c.height + i * 2 - 8);
                    } else if (i >= n - 20) {
                        g.fillRect(i, 0 - (n - i) + 4, 1, c.height + (n - i) * 2 - 8);
                    } else {
                        g.fillRect(i, 0, 1, c.height);
                    }
                }
            }

            colorBarUnitUpdate(products.getUnitList(grid.type)[0]);
        }
    }

    /**
     * Extract the date the grids are valid, or the current date if no grid is available.
     * UNDONE: if the grids hold unloaded products, then the date can be extracted from them.
     *         This function would simplify nicely.
     */

    /*
     * sainy: menu Date 
     */
    function validityDate(grids) {
        // When the active layer is considered "current", use its time as now, otherwise use current time as
        // now (but rounded down to the nearest three-hour block).

        var THREE_HOURS = 3 * HOUR;
        var now = grids ? grids.primaryGrid.date.getTime() : Math.floor(Date.now() / THREE_HOURS) * THREE_HOURS;
        var parts = configuration.get("date").split("/");  // yyyy/mm/dd or "current"
        var hhmm = configuration.get("hour");

        return parts.length > 1 ?
            Date.UTC(+parts[0], parts[1] - 1, +parts[2], +hhmm.substring(0, 2)) :
            parts[0] === "current" ? now : null;

    }

    /**
     * Display the grid's validity date in the menu. Allow toggling between local and UTC time.
     */
    function showDate(grids) {
        //sainy!change from Local to UTC datetime
        //var date = new Date(validityDate(grids)), isLocal = d3.select("#data-date").classed("local");
        //var formatted = isLocal ? µ.toLocalISO(date) : µ.toUTCISO(date);
        //d3.select("#data-date").text(formatted + " " + (isLocal ? "Local" : "UTC"));
        //d3.select("#toggle-zone").text("⇄ " + (isLocal ? "UTC" : "Local"));

        var date = new Date(validityDate(grids)), isLocal = d3.select("#data-date").classed("local");
        var formatted = isLocal ? µ.toUTCISO(date) : µ.toLocalISO(date);
        // var formatted = isLocal ? µ.toUTCISO(date, "01", "00") : µ.toLocalISO(date, "01", "00");
        d3.select("#data-date").text(formatted + " " + (isLocal ? "UTC" : "Local"));
        d3.select("#toggle-zone").text(isLocal ? "Local" : "UTC");

    }

    /**
     * Display the grids' types in the menu.
     */
    // function showGridDetails(grids) {
    //     showDate(grids);
    //     var description = "", center = "";
    //     if (grids) {
    //         var langCode = d3.select("body").attr("data-lang") || "en";
    //         var pd = grids.primaryGrid.description(langCode), od = grids.overlayGrid.description(langCode);
    //         description = od.name + od.qualifier;
    //         if (grids.primaryGrid !== grids.overlayGrid) {
    //             // Combine both grid descriptions together with a " + " if their qualifiers are the same.
    //             description = (pd.qualifier === od.qualifier ? pd.name : pd.name + pd.qualifier) + " + " + description;
    //         }
    //         center = grids.overlayGrid.source;
    //     }
    //     d3.select("#data-layer").text(description);
    //     d3.select("#data-center").text(center);
    // }

    /**
     * Constructs a toggler for the specified product's units, storing the toggle state on the element having
     * the specified id. For example, given a product having units ["m/s", "mph"], the object returned by this
     * method sets the element's "data-index" attribute to 0 for m/s and 1 for mph. Calling value() returns the
     * currently active units object. Calling next() increments the index.
     */
    function createUnitToggle(id, product) {
        var units = product.units, size = units.length;
        // var index = +(d3.select(id).attr("data-index") || 0) % size;
        var index = 0;
        return {
            value: function () {
                return units[index];
            },
            next: function () {
                // d3.select(id).attr("data-index", index = ((index + 1) % size));
            }
        };
    }

    // Stores the point and coordinate of the currently visible location. This is used to update the location
    // details when the field changes.
    var activeLocation = {};

    // Stores the point and coordinate for cross section (profiling)
    var activeLocationCrossSection = {};
    /**
     * Display a local data callout at the given [x, y] point and its corresponding [lon, lat] coordinates.
     * The location may not be valid, in which case no callout is displayed. Display location data for both
     * the primary grid and overlay grid, performing interpolation when necessary.
     */
    // function showLocationDetails(point, coord) {
    //     console.log('showLocationDetails in!', point, coord);
    //     if (configuration.get("crossSectionMode") || configuration.get("areaMeanMode")) return;
    //     // showAverageAnnualTimeSeriesAtLocation(point, coord);
    //     // showAnnualTimeSeriesAtLocation(point, coord);
    // }

    // function showTimeSeriesWindAtLocation(product, climateData, trNum, nameId, unitsId, tableId, langCode) {
    //     var unitToggle = createUnitToggle(unitsId, product), units = unitToggle.value();
    //     var t = document.getElementById(tableId);
    //     var trs = t.rows;
    //     if (trs && trs[trNum] !== undefined) {
    //         var tds = trs[trNum].cells;
    //         for (var i = 0, j = 0; i < climateData.data.length; i++) {
    //             var wind = climateData.data[j++].primary.interpolates;
    //             tds[i].innerText = (wind === null || wind[2] === null || isNaN(wind[2])) ? "n/a" : µ.formatVector(wind, units);
    //         }
    //     }
    //     d3.select(nameId).text(climateData.header.primary.description(langCode).name);
    //     d3.select(unitsId).text(units.label).on("click", function () {
    //         unitToggle.next();
    //         showTimeSeriesWindAtLocation(product, climateData, trNum, nameId, unitsId, tableId, langCode);
    //     });
    // }

    // function showTimeSeriesOverlayValueAtLocation(product, climateData, trNum, nameId, unitsId, tableId, langCode) {
    //     var unitToggle = createUnitToggle(unitsId, product), units = unitToggle.value();
    //     var t = document.getElementById(tableId);
    //     var trs = t.rows;
    //     if (trs && trs[trNum] !== undefined) {
    //         var tds = trs[trNum].cells;
    //         for (var i = 0; i < climateData.data.length; i++) {
    //             var overlay = climateData.data[i].overlay.interpolates;
    //             tds[i].innerText = (overlay === null || isNaN(overlay)) ? "n/a" : µ.formatScalar(parseFloat(overlay), units);
    //         }
    //     }
    //     d3.select(nameId).text(climateData.header.overlay.description(langCode).name);
    //     d3.select(unitsId).text(units.label).on("click", function () {
    //         unitToggle.next();
    //         showTimeSeriesOverlayValueAtLocation(product, climateData, trNum, nameId, unitsId, tableId, langCode);
    //     });
    // }

    // function showAverageAnnualTimeSeriesAtLocation(point, coord) {
    //     if (coord === undefined || coord === [] || isNaN(coord[0])) {
    //         return;
    //     }
    //     var showAvgAnnualData = configuration.get("showAvgAnnualData");
    //
    //     if (showAvgAnnualData) {
    //         showAverageVicennialTimeSeriesDetailsAtLocation(point, coord);
    //
    //     return;
    //     }
    //
    //     point = point || [];
    //     coord = coord || [];
    //     var grids = gridAgent.value(), field = fieldAgent.value(), λ = coord[0], φ = coord[1];
    //
    //     if (!field || !field.isInsideBoundary(point[0], point[1])) {
    //         return;
    //     }
    //
    //     activeLocation = {point: point, coord: coord, year: activeLocation.year};  // remember where the current location is
    //     var climateData = {
    //         header: {
    //             surface: null,
    //             level: null,
    //             overlayType: null,
    //             primary: {description: {}, units: []},
    //             overlay: {description: {}, units: []},
    //             coord: null
    //         },
    //         data: []
    //     };
    //
    //     var langCode = d3.select("body").attr("data-lang") || "en";
    //     var pd = grids.primaryGrid.description, od = grids.overlayGrid.description;
    //     climateData.header.primary.description = pd;
    //     climateData.header.overlay.description = od;
    //
    //     var primaryUnitToggle = createUnitToggle("#location-info-wind-units", grids.primaryGrid),
    //         primaryUnits = primaryUnitToggle.value();
    //     var overlayUnitToggle = primaryUnitToggle, overlayUnits = primaryUnits;
    //     var currentPrimaryValue = grids.primaryGrid.interpolate(λ, φ);
    //     var overlayAvailable = false;
    //     var currentOverlayValue = "";
    //
    //     if (grids.overlayGrid !== grids.primaryGrid && configuration.get("overlayType") !== "off") {
    //         if (µ.isValue(grids.overlayGrid.interpolate(λ, φ))) {
    //             currentOverlayValue = grids.overlayGrid.interpolate(λ, φ).toFixed(1);
    //             overlayUnitToggle = createUnitToggle("#location-info-overlay-units", grids.overlayGrid);
    //             overlayUnits = overlayUnitToggle.value();
    //             overlayAvailable = true;
    //         }
    //     }
    //
    //     moveMenuLocation(true, false);
    //     clearAvgAnnualLocationDetails(false, overlayAvailable);  // clean the slate
    //
    //     var scale = grids.overlayGrid.scale;
    //     var gradient, overlayRgb;
    //
    //     if (overlayAvailable) {
    //         gradient = scale.gradient(currentOverlayValue, 1);
    //         overlayRgb = overlayAvailable ? " color:" + "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")" + ";" : "";
    //         overlayRgb += " text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); -webkit-text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); ";
    //     }
    //
    //     d3.select("#detail-data-table").attr("style", "width: 47px;");
    //     var thead = d3.select("#detail-data-table").select("thead");
    //     var tbody = d3.select("#detail-data-table").select("tbody");
    //
    //     var trYear = tbody.selectAll("tr:nth-child(1)");
    //     var trWind = tbody.selectAll("tr:nth-child(2)");
    //     var trOverlay = tbody.selectAll("tr:nth-child(3)");
    //
    //     var trYearTdsLength = trYear.selectAll("td")._groups[0].length;
    //
    //     if (trYearTdsLength > 1) {
    //         for (var i = 2; i <= trYearTdsLength; i++) {
    //             trYear.selectAll("td:nth-child(2)").remove();
    //             trWind.selectAll("td:nth-child(2)").remove();
    //             trOverlay.selectAll("td:nth-child(2)").remove();
    //         }
    //     }
    //
    //     var overlays = [];
    //     var tdStyle = " border-left: 1px solid rgba(63, 80, 80, 0.65);";
    //     trOverlay.selectAll("td:nth-child(1)").attr("style", tdStyle + overlayRgb);
    //
    //
    //     var dataLength = climateData.data.length, dataCount = 0;
    //
    //     var unshiftData = {
    //         date: "",
    //         primary: {},
    //         overlay: {}
    //     };
    //     unshiftData.primary = {
    //         field: "",
    //         type: "",
    //         interpolates: currentPrimaryValue
    //     };
    //     unshiftData.overlay = {
    //         field: "",
    //         type: "",
    //         interpolates: currentOverlayValue
    //     };
    //
    //     climateData.data.push(unshiftData);
    //
    //     showTimeSeriesWindAtLocation(grids.primaryGrid, climateData, 1, "#location-info-wind-name", "#location-info-wind-units", "detail-data-table", langCode);
    //     if (overlayAvailable && overlays[0] !== "") {
    //         showTimeSeriesOverlayValueAtLocation(grids.overlayGrid, climateData, 2, "#location-info-overlay-name", "#location-info-overlay-units", "detail-data-table", langCode);
    //     }
    //
    //     if (_.isFinite(λ) && _.isFinite(φ)) {
    //         d3.select("#location-info-coord").html(µ.formatCoordinates(λ, φ, "<br />"));
    //         d3.select("#timeseries-avg-annual-details").classed("invisible", false);
    //         d3.select("#timeseries-avg-annual-close").classed("invisible", false);
    //         d3.select("#timeseries-avg-annual").style("height", "212px");
    //     }
    // }

    // function showAverageVicennialTimeSeriesDetailsAtLocation(point, coord) {
    //     var changed = _.keys(configuration.changedAttributes()), dateOnlyUpdated = false;
    //     if (_.intersection(changed, ["date", "hour"]).length > 0 && configuration.get("prevDate") !== configuration.get("date")) {
    //         dateOnlyUpdated = true;
    //         configuration.save("prevDate", configuration.get("date"));
    //     }
    //
    //     point = point || [];
    //     coord = coord || [];
    //     var grids = gridAgent.value(), field = fieldAgent.value(), λ = coord[0], φ = coord[1];
    //
    //     if (!field || !field.isInsideBoundary(point[0], point[1])) {
    //         return;
    //     }
    //
    //     var primaryUnitToggle = createUnitToggle("#location-info-wind-units", grids.primaryGrid),
    //         primaryUnits = primaryUnitToggle.value();
    //     var overlayUnitToggle = primaryUnitToggle, overlayUnits = primaryUnits;
    //     var currentPrimaryValue = grids.primaryGrid.interpolate(λ, φ);
    //     var overlayAvailable = false;
    //     var currentOverlayValue = "";
    //
    //     if (grids.overlayGrid !== grids.primaryGrid && configuration.get("overlayType") !== "off") {
    //         currentOverlayValue = grids.overlayGrid.interpolate(λ, φ) === null ? null : grids.overlayGrid.interpolate(λ, φ).toFixed(1);
    //         if (µ.isValue(currentOverlayValue)) {
    //             overlayUnitToggle = createUnitToggle("#location-info-overlay-units", grids.overlayGrid);
    //             overlayUnits = overlayUnitToggle.value();
    //             overlayAvailable = true;
    //         }
    //     }
    //
    //     moveMenuLocation(true, false);
    //     clearAvgAnnualLocationDetails(false, overlayAvailable);  // clean the slate
    //
    //     d3.select("#detail-data-table").attr("style", "width: 600px;");
    //     var thead = d3.select("#detail-data-table").select("thead");
    //     var tbody = d3.select("#detail-data-table").select("tbody");
    //     var trYear, trWind, trOverlay, tdStyle = "";
    //
    //     trYear = tbody.selectAll("tr:nth-child(1)");
    //     trWind = tbody.selectAll("tr:nth-child(2)");
    //     trOverlay = tbody.selectAll("tr:nth-child(3)");
    //
    //     activeLocation = {point: point, coord: coord, year: activeLocation.year};  // remember where the current location is
    //
    //     var scale = grids.overlayGrid.scale;
    //     var gradient, overlayRgb;
    //
    //     if (overlayAvailable) {
    //         gradient = scale.gradient(currentOverlayValue, 1);
    //         overlayRgb = overlayAvailable ? " color:" + "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")" + ";" : "";
    //         overlayRgb += " text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); -webkit-text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); ";
    //
    //         tdStyle = " border-left: 1px solid rgba(63, 80, 80, 0.65);";
    //         trOverlay.selectAll("td:nth-child(1)").attr("style", tdStyle + overlayRgb);
    //     }
    //
    //     if (trYear.selectAll("td")._groups[0].length === 1) {
    //         for (var i = 0; i < 12; i++) {
    //             tdStyle = "";
    //
    //             trYear.append("td").attr("style", tdStyle).text(TIMESERIES_START_YEAR + i * 20);
    //             trWind.append("td").attr("style", tdStyle).text("")
    //             trOverlay.append("td").attr("class", "td-overlay").attr("style", tdStyle).text("");
    //         }
    //     }
    //
    //     var promise = new window.Promise(function (resolve, reject) {
    //         resolve(loadAverageVicennialTimeSeriesData());
    //     });
    //
    //     promise.then(function (climateData) {
    //         if (!climateData.data || climateData.data.length === 0) {
    //             return;
    //         }
    //         var overlays = [], overlaysInterp = [];
    //         var dataLength = climateData.data.length, dataCount = 0;
    //
    //         climateData.data.forEach(function (timeseriesGrids) {
    //             if (timeseriesGrids.date !== undefined) {
    //                 dataCount++;
    //                 // tdStyle = "";
    //                 var year = timeseriesGrids.date;
    //                 var windInterpolates = timeseriesGrids.primary.interpolates;
    //                 var wind = windInterpolates[2] === null ? "n/a" : windInterpolates[2].toFixed(1);
    //                 var overlayInterpolates = timeseriesGrids.overlay.interpolates;
    //                 var overlay = overlayInterpolates === null ? "n/a" : overlayInterpolates.toFixed(1);
    //                 overlays.push(overlay);
    //                 overlaysInterp.push(timeseriesGrids.overlay.interpolates);
    //
    //                 gradient = scale.gradient(timeseriesGrids.overlay.interpolates, 1);
    //                 overlayRgb = overlayAvailable ? " color:" + "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")" + ";" : "";
    //                 overlayRgb += " text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); -webkit-text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); ";
    //
    //                 trOverlay.selectAll("td:nth-child(" + (dataCount + 1) + ")").attr("style", tdStyle + overlayRgb);
    //             }
    //         });
    //
    //         var unshiftData = {
    //             date: "",
    //             primary: {},
    //             overlay: {}
    //         };
    //         unshiftData.primary = {
    //             field: "",
    //             type: "",
    //             interpolates: currentPrimaryValue
    //         };
    //         unshiftData.overlay = {
    //             field: "",
    //             type: "",
    //             interpolates: currentOverlayValue
    //         };
    //
    //         var langCode = d3.select("body").attr("data-lang") || "en";
    //         var pd = grids.primaryGrid.description, od = grids.overlayGrid.description;
    //         climateData.header.primary.description = pd;
    //         climateData.header.overlay.description = od;
    //
    //         climateData.data.unshift(unshiftData);
    //         // showTimeSeriesWindAtLocation(grids.primaryGrid, climateData, 1, "#location-info-wind-name", "#location-info-wind-units", "detail-data-table", langCode);
    //         if (overlayAvailable && overlays[0] !== "") {
    //             showTimeSeriesOverlayValueAtLocation(grids.overlayGrid, climateData, 2, "#location-info-overlay-name", "#location-info-overlay-units", "detail-data-table", langCode);
    //             showTimeSeriesOverlayChart(grids.overlayGrid, overlays, "#location-info-overlay-units", "#overlay-chart", overlaysInterp);
    //         }
    //
    //         if (_.isFinite(λ) && _.isFinite(φ)) {
    //             d3.select("#location-info-coord").html(µ.formatCoordinates(λ, φ, "<br />"));
    //             d3.select("#timeseries-avg-annual-details").classed("invisible", false);
    //             d3.select("#timeseries-avg-annual-close").classed("invisible", false);
    //             d3.select("#timeseries-avg-annual").style("height", "212px");
    //         }
    //
    //     }, function (error) {
    //         report.error(error);
    //     });
    //
    // }

    // function showAreaMeanAnnualTimeSeriesAtLocation(point, coord) {
        // point = point[0];
        // coord = coord[0]
        // if (coord === undefined || coord === [] || coord === [NaN, NaN]) {
        //     return;
        // }
        // if (!d3.select("#timeseries-areamean-vicennial-details").classed("invisible")) return;
        //
        // var changed = _.keys(configuration.changedAttributes()), dateOnlyUpdated = false;
        // if (_.intersection(changed, ["date", "hour"]).length > 0 && configuration.get("prevDate") !== configuration.get("date")) {
        //     dateOnlyUpdated = true;
        //     configuration.save("prevDate", configuration.get("date"));
        // }
        //
        // point = point || [];
        // coord = coord || [];
        // var grids = gridAgent.value(), field = fieldAgent.value(), λ = coord[0], φ = coord[1];
        //
        // if (!field || !field.isInsideBoundary(point[0], point[1])) {
        //     return;
        // }

        // var primaryUnitToggle = createUnitToggle("#location-info-wind-units", grids.primaryGrid),
        //     primaryUnits = primaryUnitToggle.value();
        // var overlayUnitToggle = primaryUnitToggle, overlayUnits = primaryUnits;
        // var currentPrimaryValue = grids.primaryGrid.interpolate(λ, φ);
        // var overlayAvailable = false;
        // var currentOverlayValue = "";

        // if (grids.overlayGrid !== grids.primaryGrid && configuration.get("overlayType") !== "off") {
        //     currentOverlayValue = grids.overlayGrid.interpolate(λ, φ) === null ? null : grids.overlayGrid.interpolate(λ, φ).toFixed(1);
        //     if (µ.isValue(currentOverlayValue)) {
        //         overlayUnitToggle = createUnitToggle("#location-info-overlay-units", grids.overlayGrid);
        //         overlayUnits = overlayUnitToggle.value();
        //         overlayAvailable = true;
        //     }
        // }

        // moveMenuLocation(true, false);
        // // clearAvgAnnualLocationDetails(false, overlayAvailable);  // clean the slate
        // clearLocationDetailsAreaMean(false);
        // d3.select("#areamean-detail-data-table").attr("style", "width: 600px;");
        // var thead = d3.select("#areamean-detail-data-table").select("thead");
        // var tbody = d3.select("#areamean-detail-data-table").select("tbody");
        // var trYear, trWind, trOverlay, tdStyle = "";
        //
        // trYear = tbody.selectAll("tr:nth-child(1)");
        // // trWind = tbody.selectAll("tr:nth-child(2)");
        // trOverlay = tbody.selectAll("tr:nth-child(2)");
        //
        // // activeLocation = {point: point, coord: coord, year: activeLocation.year};  // remember where the current location is
        //
        // var scale = grids.overlayGrid.scale;
        // var gradient, overlayRgb;

        // if (overlayAvailable) {
        //     gradient = scale.gradient(currentOverlayValue, 1);
        //     overlayRgb = overlayAvailable ? " color:" + "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")" + ";" : "";
        //     overlayRgb += " text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); -webkit-text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); ";
        //
        //     tdStyle = " border-left: 1px solid rgba(63, 80, 80, 0.65);";
        //     trOverlay.selectAll("td:nth-child(1)").attr("style", tdStyle + overlayRgb);
        // }
        // if (trYear.selectAll("td")._groups[0].length === 0) {
        //     for (var i = 0; i < 12; i++) {
        //         tdStyle = "";
        //         trYear.append("td").attr("style", tdStyle).text(TIMESERIES_START_YEAR + i * 20);
        //         // trWind.append("td").attr("style", tdStyle).text("")
        //         trOverlay.append("td").attr("class", "td-overlay").attr("style", tdStyle).text("");
        //     }
        // }
//추가..
        // if (!configuration.get("showAreaMean")) return;

        // let coords = activeLocationCrossSection.coords || [];
        // if (coords.length === 0) return;
        //
        // const params = configuration.get("date").replace(/\//gi, "-")
        //     + "/" + ((configuration.get("date") === "current") ? "0000" : configuration.get("hour"))
        //     + "/" + configuration.get("param")
        //     + "/" + configuration.get("surface")
        //     + "/" + configuration.get("level")
        //     + "/overlay=" + configuration.get("overlayType")
        //     + "/" + coords[0][0].toFixed(3)
        //     + "/" + coords[0][1].toFixed(3)
        //     + "/" + coords[1][0].toFixed(3)
        //     + "/" + coords[1][1].toFixed(3)
        //     + "/" + configuration.get("scenario");
        // let crossSection = [
        //     coords[0][0].toFixed(3),
        //     coords[0][1].toFixed(3),
        //     coords[0][0] === coords[1][0] ? (coords[1][0] - 0.002).toFixed(3) : coords[1][0].toFixed(3),
        //     coords[1][1].toFixed(3)
        // ].join(",");
        //
        // let promise = new window.Promise(function (resolve, reject) {
        //     resolve(mcUtils.loadAreaMeanData(params, crossSection));
        // });
        //
        // promise.then(function (climateData) {
        //     if (!climateData.data || climateData.data.length === 0) {
        //         return;
        //     }
        //     var overlays = [], overlaysInterp = [];
        //     let dataCount = 0;
        //     climateData.data.forEach(function (timeseriesGrids) {
        //         if (timeseriesGrids.date !== undefined) {
        //             dataCount++;
        //             tdStyle = "";
        //
        //             var overlayInterpolates = timeseriesGrids.overlay.interpolates;
        //             var overlay = overlayInterpolates === null ? "n/a" : overlayInterpolates.toFixed(1);
        //             overlays.push(overlay);
        //             overlaysInterp.push(timeseriesGrids.overlay.interpolates);
        //
        //             gradient = scale.gradient(timeseriesGrids.overlay.interpolates, 1);
        //             overlayRgb = " color:" + "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")" + ";";
        //             overlayRgb += " text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); -webkit-text-shadow: 0.4px 0.4px 1px rgba(255, 255, 255, 1.0); ";
        //
        //             trOverlay.selectAll("td:nth-child(" + (dataCount) + ")").attr("style", tdStyle + overlayRgb);
        //         }
        //     });
        //
        //     var langCode = d3.select("body").attr("data-lang") || "en";
        //     const od = grids.overlayGrid.description;
        //     climateData.header.overlay.description = od;
        //
        //     if (overlays[0] !== "") {
        //         showTimeSeriesOverlayValueAtLocation(grids.overlayGrid, climateData, 1, "#areamean-info-overlay-name", "#areamean-info-overlay-units", "areamean-detail-data-table", langCode);
        //         showTimeSeriesOverlayChart(grids.overlayGrid, overlays, "#areamean-info-overlay-units", "#areamean-overlay-chart", overlaysInterp);
        //     }
        //     if (_.isFinite(coords[0][0]) && _.isFinite(coords[0][1]) && _.isFinite(coords[1][0]) && _.isFinite(coords[1][1])) {
        //         let coords = activeLocationCrossSection.coords;
        //         let formattedCoords = µ.formatCoordinates(coords[0][0], coords[0][1], ", ") + "<br />" + µ.formatCoordinates(coords[1][0], coords[1][1], ", ");
        //         d3.select("#areamean-location-info-coord").html(formattedCoords);
        //         d3.select("#timeseries-areamean-vicennial-details").classed("invisible", false);
        //         d3.select("#timeseries-areamean-vicennial-close").classed("invisible", false);
        //         d3.select("#timeseries-areamean-vicennial").style("height", "212px");
        //     }
        //
        // }, function (error) {
        //     report.error(error);
        // });
    // }

    // function showLocationDetailsCrossSection() {
    //     if (!configuration.get("crossSectionMode")) return;
    //
    //     if (activeLocationCrossSection.coords && activeLocationCrossSection.coords.length === 2) {
    //         let coords = activeLocationCrossSection.coords;
    //         let formattedCoords = µ.formatCoordinates(coords[0][0], coords[0][1], ", ") + "<br />" + µ.formatCoordinates(coords[1][0], coords[1][1], ", ");
    //         d3.select("#location-info-coord-cs").html(formattedCoords);
    //         d3.select("#location-details-cs").classed("invisible", false);
    //         d3.select("#location-cs-close").classed("invisible", false);
    //     }
    // }
    //
    // function showLocationDetailsAreaMean() {
    //     if (!configuration.get("areaMeanMode")) return;
    //     if (activeLocationCrossSection.coords && activeLocationCrossSection.coords.length === 2) {
    //         // showAreaMeanAnnualTimeSeriesAtLocation(activeLocationCrossSection.points, activeLocationCrossSection.coords);
    //
    //     }
    // }
    //
    // function updateLocationDetails(fullUpdate) {
    //     var changed = _.keys(configuration.changedAttributes()), fullUpdateRequired = false;
    //
    //     if (_.intersection(changed, ["date", "hour", "param", "surface", "level", "overlayType", "scenario"]).length > 0) {
    //         fullUpdateRequired = true;
    //     }
    //
    //     if (fullUpdate && fullUpdateRequired) {
    //         // showLocationDetails(activeLocation.point, activeLocation.coord);
    //         showLocationDetailsAreaMean()
    //     }
    // }

    // function moveMenuLocation(move, year) {
    //     if (year) {
    //         d3.select("#details").style("bottom", move ? "260px" : "260px");
    //     } else {
    //         d3.select("#details").style("bottom", move ? "260px" : "25px");
    //     }
    // }

    function clearAvgAnnualLocationDetails(clearEverything, overlayAvailable) {
        if (clearEverything) {
            activeLocation = {};
            µ.clearCanvas(d3.select("#locationpoint").node());
        }

        // if (!overlayAvailable) {
        //
        //     // clear timeseries chart
        //     // µ.removeChildren(d3.select("#overlay-chart").node());
        // }
    }

    function clearLocationDetailsCrossSection(clearEverything) {
        let crossSectionOverlay = crossSectionOverlayAgent.value();
        if (crossSectionOverlay) {
            crossSectionOverlay.clearResource();
        }
        let quickoverlay = quickoverlayAgent.value();
        if (quickoverlay) {
            quickoverlay.clearCrossSection();
        }
        configuration.save({ showCrossSection: false });
        µ.clearCanvas(d3.select("#locationpoint").node());

        if (clearEverything) {
            // d3.select("#location-cs-close").classed("invisible", true);
            // d3.select("#location-details-cs").classed("invisible", true);

            activeLocationCrossSection.coords = [];
            activeLocationCrossSection.points = [];
        }
    }

    function clearLocationDetailsAreaMean(clearEverything) {
        configuration.save({ showAreaMean: false });


        if (clearEverything) {
            // d3.select("#timeseries-areamean-vicennial-close").classed("invisible", true);
            // d3.select("#timeseries-areamean-vicennial-details").classed("invisible", true);
            // d3.select("#timeseries-areamean-vicennial").style("height", "0px");
            µ.clearCanvas(d3.select("#locationpoint").node());

            activeLocationCrossSection.coords = [];
            activeLocationCrossSection.points = [];
        }
        //
        // // clear chart
        // µ.removeChildren(d3.select("#areaMean").node());
        // d3.select("#location-coord").text("");
        // // d3.select("#location-close").classed("invisible", true);
        // d3.select("#location-wind").text("");
        // d3.select("#location-wind-units").text("");
        // d3.select("#location-value").text("");
        // d3.select("#location-value-units").text("");
        // if (clearEverything) {
        //     moveMenuLocation(false, false);
        // }

        // if (!overlayAvailable) {
        //     // clear location-info-overlay
        //     d3.select("#areamean-info-overlay-name").text("");
        //     d3.select("#areamean-info-overlay-units").text("");
        //
        //     var tbody = d3.select("#areamean-detail-data-table").select("tbody");
        //     var trOverlay = tbody.selectAll("tr:nth-child(2)");
        //     var trOverlayTdsLength = trOverlay.selectAll("td")._groups[0].length;
        //     if (trOverlayTdsLength > 0) {
        //         for (var i = 0; i < trOverlayTdsLength; i++) {
        //             trOverlay.selectAll("td:nth-child(" + (i + 1) + ")").text("");
        //         }
        //     }
        //
        //     // clear timeseries chart
        //     µ.removeChildren(d3.select("#areamean-overlay-chart").node());
        // }
    }

    function stopCurrentAnimation(alsoClearCanvas) {
        animatorAgent.cancel();
        // const periodPlay = configuration.get("periodPlay");
        if (alsoClearCanvas) {
           µ.clearCanvas(d3.select("#animation").node());
        }
    }

    /**
     * Load average vicennial time series data when point is clicked
     *
     */
    function loadAverageVicennialTimeSeriesData() {
        var params = "/" + µ.isMobile()
            + "/" + configuration.get("date").replace(/\//gi, "-")
            + "/" + ((configuration.get("date") === "current") ? "0000" : configuration.get("hour"))
            + "/" + configuration.get("param")
            + "/" + configuration.get("surface")
            + "/" + configuration.get("level")
            + "/overlay=" + configuration.get("overlayType")
            + "/" + activeLocation.coord[0]
            + "/" + activeLocation.coord[1]
            + "/" + configuration.get("scenario");
        report.status("Downloading...");
        // log.time("loading average vicennial time series");
        return µ.loadJson(µ.timeseriesAvgVicennial + params).then(function (climateData) {
            report.status("");
            // log.timeEnd("loading average vicennial time series");
            return climateData;
        });
    }

    function showTimeSeriesOverlayChart(climateData, chart, clientWidth, clientHeight, colorGradientList) {
        var overlays = [];
        climateData.data.forEach(function (timeseriesGrids) {
            if (timeseriesGrids.date !== undefined) {
                //벡터 데이터는 인터폴레이트 값이 배열형태이고 3번째 인자이다.
                //스칼라 데이터는 인터폴레이트 값이 그냥 숫자데이터이다.
                let overlayInterpolates = _.isArray(timeseriesGrids.overlay.interpolates) ? timeseriesGrids.overlay.interpolates[2] : timeseriesGrids.overlay.interpolates;
                var overlay = overlayInterpolates === null ? "n/a" : overlayInterpolates.toFixed(2);
                overlays.push(overlay);
            }
        });
        const data = overlays;
        if (!data) return;
        // var unitToggle = createUnitToggle(units, product), units = unitToggle.value();
        var grid = (gridAgent.value() || {}).overlayGrid;
        if (colorGradientList) data.map((overlay) => colorGradientList.push(grid.scale.gradient(overlay, 1)));
        var minY = Math.min.apply(null, data) * 0.99;
        var maxY = Math.max.apply(null, data);
        var maxColor = grid.scale.gradient(Math.max.apply(null, overlays), 1),
            minColor = grid.scale.gradient(Math.min.apply(null, overlays), 1);
        // var width = (data.length + 1) * 56, height = overlays
        const width = clientWidth;
        const height = clientHeight;

        // var offSet = (chart === "#overlay-chart" || chart ==="#areamean-overlay-chart") ? 46 : 0;
        var offSet = clientWidth / 24;
        var x = d3.scaleLinear()
            .range([offSet, width + offSet])
            .domain([0, data.length]);

        var y = d3.scaleLinear()
            .range([height, 5])
            .domain([minY, maxY]);

        var line = d3.area()
            .x(function (d, i) {
                return x(i);
            })
            .y1(function (d) {
                return y(d);
            })
            .y0(height)
            .curve(d3.curveCardinal);

        var svg = d3.select(chart);
        svg.selectAll("*").remove();

        var defs = svg.append("defs");

        var areaGradient = defs.append("linearGradient")
            .attr("id", "areaGradient")
            .attr("x1", "0%").attr("y1", "0%")
            .attr("x2", "0%").attr("y2", "100%");

        areaGradient.append("stop")
            .attr("offset", "50%")
            .attr("stop-color", "rgba(" + maxColor[0] + "," + maxColor[1] + "," + maxColor[2] + "," + maxColor[3] + ")")
            .attr("stop-opacity", 0.9);

        areaGradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", "rgba(" + minColor[0] + "," + minColor[1] + "," + minColor[2] + "," + minColor[3] + ")")
            // .attr("stop-color", "rgba(255,255,255,255)")
            .attr("stop-opacity", 0.05);

        svg.append("g").append("path")
            .datum(data)
            .attr("class", "overlay-chart-line")
            .attr("style", "fill: url(#areaGradient")
            .attr("d", line);
    }

    function renderCrossSectionOverlay(crossSectionData, crossSectionOverlay) {
        if (!configuration.get("showCrossSection")) return;
        let type = configuration.get("overlayType") === 'default' ? 'wind' : configuration.get("overlayType");
        let globe = globeAgent.value();
        let grids = gridAgent.value();

        let primaryGrid = grids.primaryGrid;
        let overlayGrid = grids.overlayGrid;
        let hasDistinctOverlay = primaryGrid !== overlayGrid;
        let scale = hasDistinctOverlay ? overlayGrid.scale : primaryGrid.scale;
        let binaryScale = hasDistinctOverlay ? overlayGrid.binaryScale : primaryGrid.binaryScale;
        binaryScale = binaryScale === undefined ? 1. : binaryScale;
        let verticalOverlay = hasDistinctOverlay ? overlayGrid.vertical : primaryGrid.vertical;
        let crossSectionScale = hasDistinctOverlay ? overlayGrid.crossSectionScale : primaryGrid.crossSectionScale;
        if (!verticalOverlay) {
            clearLocationDetailsCrossSection(false);
        }

        if (verticalOverlay) {
                if (!crossSectionData) return;
                scale = crossSectionScale === undefined ? scale : crossSectionScale(crossSectionData.data, binaryScale);

                // clear all rendered cross section (2d, 3d)
                // let crossSectionOverlay = crossSectionOverlayAgent.value(); // for 2d
                let quickoverlay = quickoverlayAgent.value();

                if (crossSectionOverlay) {
                    crossSectionOverlay.clearResource();
                }
                if (quickoverlay) {
                    quickoverlay.clearCrossSection();
                }

                // render 2d or 3d by projection
                if (globe.quickoverlay) { // 3d
                    // const changed = _.keys(configuration.changedAttributes());
                    // if (_.intersection(changed, ["orientation"]).length > 0) return;
                    //
                    if (quickoverlay) {
                        quickoverlay.renderCrossSection(crossSectionData, grids.overlayGrid, scale);
                    }
                } else { // 2d
                    if (crossSectionOverlay) {
                        crossSectionOverlay.render(crossSectionData, scale, binaryScale, type);
                    }
                }
        }
    }

    function loadAverageVicennialTimeSeriesDataMultipleOverlay() {
        var params = "/" + µ.isMobile()
            + "/" + configuration.get("date").replace(/\//gi, "-")
            + "/" + ((configuration.get("date") === "current") ? "0000" : configuration.get("hour"))
            + "/" + configuration.get("param")
            + "/" + configuration.get("surface")
            + "/" + configuration.get("level")
            + "/overlay=" + configuration.get("overlayType")
            + "/" + activeLocation.coord[0]
            + "/" + activeLocation.coord[1]
            + "/" + configuration.get("scenario");
        report.status("Downloading...");
        // log.time("loading average vicennial time series multiple overlay");
        return µ.loadJson(µ.timeseriesAvgVicennialMultipleOverlay + params).then(function (climateData) {
            report.status("");

            // log.timeEnd("loading average vicennial time series multiple overlay");
            return climateData;
        });
    }

    function loadScreenCaptureImage() {
        var params = µ.currentHash();

        report.status("Downloading...");
        return µ.loadImage(µ.screenCapture + encodeURI(params).replace(/\//gi, "'")).then(function (image) {
            report.status("");
            return image;
        });
    }

    /**
     * Registers a click event handler for the specified DOM element which modifies the configuration to have
     * the attributes represented by newAttr. An event listener is also registered for configuration change events,
     * so when a change occurs the button becomes highlighted (i.e., class ".highlighted" is assigned or removed) if
     * the configuration matches the attributes for this button. The set of attributes used for the matching is taken
     * from newAttr, unless a custom set of keys is provided.
     */
    // function bindButtonToConfiguration(elementId, newAttr, keys) {
    //     keys = keys || _.keys(newAttr);
    //     d3.select(elementId).on("click", function () {
    //         if (elementId === "#scenario-ssp585" || elementId === "#scenario-ssp126" ||
    //             elementId === "#overlay-total_precipitable_water" || elementId === "#overlay-sea_ice_area") {
    //             // let value0 = 0;
    //             // var label = altitudeController.labels()[value0];
    //             // d3.select("#altitude-list").property("value", value0);
    //             // d3.select(".altitude-value").text(label);
    //             // d3.select("#altitude-list").attr("disabled", "disabled");
    //             newAttr.surface = "surface";
    //             newAttr.level = "level";
    //         } else {
    //             d3.select("#altitude-list").attr("disabled", null);
    //         }
    //
    //         if (elementId === "#scenario-ssp585" || elementId === "#scenario-ssp126") {
    //             // d3.select("#ocean-mode-enable").classed("invisible", true);
    //             if (configuration.get("param") === "ocean") {
    //                 newAttr.param = "wind";
    //                 newAttr.surface = "surface";
    //                 newAttr.level = "level";
    //                 newAttr.overlayType = "default";
    //             } else {
    //                 newAttr.overlayType = configuration.get("overlayType");
    //             }
    //         }
    //
    //         if (d3.select(elementId).classed("disabled")) return;
    //
    //         if (elementId === "#scenario-ssp585" || elementId === "#scenario-ssp126") {
    //             let attr = _.clone(configuration.attributes);
    //             let selectedDate = attr.date;
    //             let clickedScenario = elementId.substring("#scenario-".length, elementId.length);
    //             let scenarioStartDate = products.scenarioPeriod(clickedScenario).startDate;
    //
    //             if (selectedDate < scenarioStartDate) {
    //                 let currentDate = µ.dateToUTCymd(new Date(Date.now()), "/");
    //                 newAttr.date = currentDate.substring(0, 8) + "01";
    //             }
    //         }
    //
    //         configuration.save(newAttr);
    //     });
    //     configuration.on("change", function (model) {
    //         var attr = model.attributes;
    //         d3.select(elementId).classed("highlighted", _.isEqual(_.pick(attr, keys), _.pick(newAttr, keys)));
    //         // if(attr && attr.surface && attr.level !== undefined){
    //         //     var surfaceLevel = [attr.surface, attr.level].join("-");
    //         //     var altitudeSliderValue = (_.invert(altitudeController.list()))[surfaceLevel];
    //         //     var label = altitudeController.labels()[altitudeSliderValue];
    //         //     d3.select("#altitude-list").property("value", altitudeSliderValue);
    //         //     d3.select(".altitude-value").text(altitudeSliderValue === "0" ? label : label + " hPa");
    //         // }
    //     });
    // }

    // function reportSponsorClick(type) {
    //     if (ga) {
    //         ga("send", "event", "sponsor", type);
    //     }
    // }

    /**
     * Registers all event handlers to bind components and page elements together. There must be a cleaner
     * way to accomplish this...
     */
    function init() {

        report.status("Initializing...");

        fitView(view);

        // Adjust size of the scale canvas to fill the width of the menu to the right of the label.

        if (!µ.isMobile()) { //PC일때
            d3.select("#scale")
                .attr("width", "12px")
                .attr("height", "230px");
        } else { //mobile일때
            d3.select("#scale")
                .attr("width", "496px")
                .attr("height", "18px");
        }


        if (µ.isFF()) {
            // Workaround FF performance issue of slow click behavior on map having thick coastlines.
            d3.select("#display").classed("firefox", true);
        }

        // Tweak document to distinguish CSS styling between touch and non-touch environments. Hacky hack.
        if ("ontouchstart" in document.documentElement) {
            d3.select(document).on("touchstart", function () {
            });  // this hack enables :active pseudoclass
        } else {
            d3.select(document.documentElement).classed("no-touch", true);  // to filter styles problematic for touch
        }

        // Bind configuration to URL bar changes.
        d3.select(window).on("hashchange", function () {
            configuration.fetch({trigger: "hashchange"});
        });

        configuration.on("change", report.reset);

        meshAgent.listenTo(configuration, "change:topology", function (context, attr) {
            meshAgent.submit(buildMesh, attr);
        });

        globeAgent.listenTo(configuration, "change:projection", function (source, attr) {
            globeAgent.submit(buildGlobe, attr);
        });

        gridAgent.listenTo(configuration, "change", function () {
            var changed = _.keys(configuration.changedAttributes()), rebuildRequired = false;

            // Build a new grid if any layer-related attributes have changed.
            if (_.intersection(changed, ["date", "hour", "param", "surface", "level", "scenario", "toggleForColorScaleUpdate"]).length > 0) {
                rebuildRequired = true;
            }
            // Build a new grid if the new overlay type is different from the current one.
            var overlayType = configuration.get("overlayType") || "default";
            if (_.indexOf(changed, "overlayType") >= 0 && overlayType !== "off") {
                var grids = (gridAgent.value() || {}), primary = grids.primaryGrid, overlay = grids.overlayGrid;

                // * Rebuild required alsolutely for quickoverly interpolation after overlay changed
                // if (!overlay) {
                //     // Do a rebuild if we have no overlay grid.
                //     rebuildRequired = true;
                // }
                // else if (overlay.type !== overlayType && !(overlayType === "default" && primary === overlay)) {
                //     // Do a rebuild if the types are different.
                rebuildRequired = true;
                // }
            }

            if (rebuildRequired) {
                //내손안의 기후변화에서는 기존에 sea_ice_area오버레이시 param이 wind로 설정되어 있다.
                //내손안의 기후변화 홈페이지의 url을 변경시킬 수는 없어서 여기에서 sea_ice_area오버레이일때 param을
                //강제로 ocean으로 변경시켜준다.
                if (overlayType === 'sea_ice_area') {
                    configuration.save({param: 'ocean'});
                }
                if (overlayType === 'slr') {
                    //slr변수일때는 데이터가 해당년 1월 밖에 없다.
                    //따라서 아래 코드를 통해 강제로 1월이 아닌 달은 1월로 변경시켜준다.
                    const current = new Date();
                    const year = current.getFullYear();
                    const date = configuration.get("date") === "current"
                        ? year + "/" + "01" + "/01"
                        : configuration.get("date");
                    const splitDate = date.split('/');
                    splitDate[1] = '01';
                    const slrDate = splitDate.join('/');
                    configuration.save({date: slrDate, hour: '0000'});
                }
                gridAgent.submit(buildGrids);
            }
        });
        // gridAgent.on("submit", function() {
        //     showGridDetails(null);
        //     setDateSliderValue(null);
        // });
        gridAgent.on("update", function (grids) {
            timeBoxUpdate();
            drawBasicLocationPoint();
        });
        // d3.select("#toggle-zone").on("click", function () {
        //     d3.select("#data-date").classed("local", !d3.select("#data-date").classed("local"));
        //     showDate(gridAgent.cancel.requested ? null : gridAgent.value());
        // });

        function startRendering() {
            rendererAgent.submit(buildRenderer, meshAgent.value(), globeAgent.value());
        }

        rendererAgent.listenTo(meshAgent, "update", startRendering);
        rendererAgent.listenTo(globeAgent, "update", startRendering);

        function startInterpolation() {
            configuration.save({interpolateEnd: false});
            fieldAgent.submit(interpolateField, globeAgent.value(), gridAgent.value());
        }

        function cancelInterpolation() {
            fieldAgent.cancel();
        }

        fieldAgent.listenTo(gridAgent, "update", startInterpolation);
        fieldAgent.listenTo(rendererAgent, "render", startInterpolation);
        fieldAgent.listenTo(rendererAgent, "start", cancelInterpolation);
        fieldAgent.listenTo(rendererAgent, "redraw", cancelInterpolation);

        animatorAgent.listenTo(fieldAgent, "update", function (field) {
            if (configuration.get("windAnimation") !== false)
            animatorAgent.submit(animate, globeAgent.value(), field, gridAgent.value());
        });
        animatorAgent.listenTo(rendererAgent, "start", stopCurrentAnimation.bind(null, true));
        animatorAgent.listenTo(gridAgent, "submit", stopCurrentAnimation.bind(null, false));
        animatorAgent.listenTo(fieldAgent, "submit", stopCurrentAnimation.bind(null, false));

        overlayAgent.listenTo(fieldAgent, "update", function () {
            overlayAgent.submit(drawOverlay, fieldAgent.value(), configuration.get("overlayType"), configuration.get("showContour"), configuration.get("showContourLabel"), configuration.get("showWindVector"));
            configuration.save({interpolateEnd: true});
        });
        overlayAgent.listenTo(rendererAgent, "start", function () {
            µ.clearCanvas(d3.select("#overlay").node());
            µ.clearCanvas(d3.select("#contour").node());
            µ.clearCanvas(d3.select("#contourLabel").node());
            overlayAgent.submit(drawOverlay, fieldAgent.value(), null, null, null, null);
        });
        overlayAgent.listenTo(configuration, "change", function () {
            var changed = _.keys(configuration.changedAttributes());
            // if only overlay relevant flags have changed...
            if (_.intersection(changed, ["overlayType", "showGridPoints", "showContour", "showContourLabel", "showWindVector"]).length > 0) {
                overlayAgent.submit(drawOverlay, fieldAgent.value(), configuration.get("overlayType"), configuration.get("showContour"), configuration.get("showContourLabel"), configuration.get("showWindVector"));
            }
        });

        function quickOverlayReady() {
            view = µ.view();
            var width = view.width, height = view.height;
            var canvas = d3.select("#quickoverlay");
            canvas.width = width;
            canvas.height = height;

            if (!checkWebGL(canvas)) {
                return;
            }

            quickoverlayAgent.submit(mcl, canvas, configuration, inputController);
        }

        // function crossSectionOverlayReady() {
        //     let width = view.width, height = view.height;
        //     let canvas = d3.select("#crossSection2d");
        //     canvas.width = width * 0.58;
        //     canvas.height = height * 0.47;
        //
        //     if (!checkWebGL(canvas)) {
        //         return;
        //     }
        //
        //     crossSectionOverlayAgent.submit(mcOverlayCs, canvas, configuration);
        // };

        quickOverlayReady();
        // crossSectionOverlayReady();

        // Add event handlers for showing, updating, and removing location details.
        // inputController.on("click", showLocationDetails); //기본모드 클릭
        // fieldAgent.on("update", _.partial(updateLocationDetails, true));
        // rendererAgent.on("redraw", _.partial(updateLocationDetails, false));
        // rendererAgent.on("render", _.partial(updateLocationDetails, false));

        // d3.select("#location-close").on("click", _.partial(clearAvgAnnualLocationDetails, true));
        // 기본모드 close 버튼
        // d3.select("#timeseries-avg-annual-close").on("click", _.partial(clearAvgAnnualLocationDetails, true));
        // d3.select("#timeseries-avg-annual").style("height", "0px");
        // 연직단면 close 버튼
        // d3.select("#location-cs-close").on("click", _.partial(clearLocationDetailsCrossSection, true));
        // 연직단면 출력
        // d3.select("#location-cs-show").html("연직단면도").on("click", function () {
        //     configuration.save({ showCrossSection: true });
        //     crossSectionOverlayRenderAgent.submit(renderCrossSectionOverlay);
        // });
        // 영역평균 close버튼
        // d3.select("#timeseries-areamean-vicennial-close").on("click", _.partial(clearLocationDetailsAreaMean, true));
        // // 영역평균 출력
        // d3.select("#location-areamean-show").html("영역별평균시계열").on("click", function () {
        //     configuration.save({ showAreaMean: true });
        //     areaMeanOverlayRenderAgent.submit(renderAreaMeanOverlay);
        // });
        // Modify menu depending on what mode we're in.
        // configuration.on("change:param", function (context, mode) {
        //     d3.selectAll(".aerchem-mode").classed("invisible", mode !== "aerchem");
        //     d3.selectAll(".ocean-mode").classed("invisible", mode !== "ocean");
        //     d3.selectAll(".wind-mode").classed("invisible", mode !== "wind");
        // });


        //sainy add mode aerchem 2017-10-31
        // d3.select("#aerchem-mode-enable").on("click", function () {
        //     if (configuration.get("param") !== "aerchem") {
        //         //configuration.save({param: "aerchem", surface: "surface", level: "level", overlayType: "default"});
        //         configuration.save({param: "aerchem", surface: "surface", level: "level", overlayType: "aod_sulf"});
        //     }
        // });

        // Add logic to disable buttons that are incompatible with each other.
        // configuration.on("change:overlayType", function (x, ot) {
        //     d3.select("#surface-level").classed("disabled", ot === "air_density" || ot === "wind_power_density");
        //     d3.select("#aerchem-isobaric-1000hPa").classed("disabled", ot == "sulphur_dioxide");
        //     d3.select("#aerchem-isobaric-850hPa").classed("disabled", ot == "sulphur_dioxide");
        //     d3.select("#aerchem-isobaric-700hPa").classed("disabled", ot == "sulphur_dioxide");
        //     d3.select("#aerchem-isobaric-500hPa").classed("disabled", ot == "sulphur_dioxide");
        //     d3.select("#aerchem-isobaric-250hPa").classed("disabled", ot == "sulphur_dioxide");
        //     d3.select("#aerchem-isobaric-70hPa").classed("disabled", ot == "sulphur_dioxide");
        //     d3.select("#aerchem-isobaric-10hPa").classed("disabled", ot == "sulphur_dioxide");
        // });

        // sainy !! default setting = disable not ready variables
        // configuration.on("change:surface", function (x, s) {
        //     d3.select("#overlay-air_density").classed("disabled", s === "isobaric" || s === "surface");
        //     d3.select("#overlay-wind_power_density").classed("disabled", s === "isobaric" || s === "surface");
        //     d3.select("#overlay-mean_sea_level_pressure").classed("disabled", s === "isobaric" || s === "surface");
        //     d3.select("#overlay-aod_total").classed("disabled", s === "isobaric" || s === "surface");
        //     d3.select("#overlay-sulphur_dioxide").classed("disabled", s === "isobaric")
        // });

        // Add event handlers for the time navigation buttons.
        // d3.select("#nav-now").on("click", function () {
        //     configuration.save({date: "current", hour: ""});
        // });

        // d3.select("#nani-now").on("click", function () {
        //     configuration.save({date: "current", hour: ""});
        // });

        // d3.select("#ngrid").on("click", function () {
        //     configuration.save({showGridPoints: !configuration.get("showGridPoints")});
        // });
        // configuration.on("change:showGridPoints", function (x, showGridPoints) {
        //     d3.select("#ngrid").classed("highlighted", showGridPoints);
        // });

        // // Add handler for contour button
        // d3.select("#option-show-contour").on("click", function () {
        //     configuration.save({
        //         showContour: !configuration.get("showContour"),
        //         showContourLabel: configuration.get("showContour") !== "on" ? "" : configuration.get("showContour")
        //     });
        // });
        // configuration.on("change:showContour", function (x, showContour) {
        //     d3.select("#option-show-contour").classed("highlighted", showContour);
        //     d3.select("#contour-isoline").property("checked", showContour === true ? true : false);
        // });
        //
        // // Add handler for contour label button
        // d3.select("#option-show-contour-label").on("click", function () {
        //     configuration.save({showContourLabel: !configuration.get("showContourLabel")});
        // });
        // configuration.on("change:showContourLabel", function (x, showContourLabel) {
        //     d3.select("#option-show-contour-label").classed("highlighted", showContourLabel);
        //     d3.select("#contour-isoline-label").property("checked", showContourLabel === true ? true : false);
        // });
        //
        // d3.select("#option-contour-thresholds").on("click", function () {
        //     this.select();
        // });
        //
        // // Add handler for wind vector button
        // d3.select("#option-show-wind-vector").on("click", function () {
        //     configuration.save({showWindVector: !configuration.get("showWindVector")});
        // });
        // configuration.on("change:showWindVector", function (x, showWindVector) {
        //     d3.select("#option-show-wind-vector").classed("highlighted", showWindVector);
        // });
        //
        // d3.select("#option-contour-thresholds").on("keyup", _.debounce(function () {
        //         overlayAgent.submit(drawOverlay, fieldAgent.value(),
        //             configuration.get("overlayType"), configuration.get("showContour"), configuration.get("showContourLabel"), configuration.get("showWindVector"));
        //         this.select();
        //     }, 1000)
        // );

        // When touch device changes between portrait and landscape, rebuild globe using the new view size.
        d3.select(window).on("resize", function () {
            orientationChanged().then(function () {
                view = µ.view();
                fitView(view);
                // drawPredictDays(staticVariable.MAX_PRODUCT_PERIOD);

                var quickoverlay = quickoverlayAgent.value();
                if (quickoverlay) {
                    quickoverlay.changeWebGlCanvas();
                    // if (µ.isMobile()) {
                    //     quickoverlay.clearResource();
                    //     quickoverlay = null;
                    // }
                }

                configuration.save({orientationchanged: true});
                globeAgent.submit(buildGlobe, configuration.get("projection"));
            });
        });

        // d3.select("#showAvgAnnualData")
        //     .on("change", _.debounce(function () {
        //         var checked = d3.select("#showAvgAnnualData").property("checked");
        //         configuration.save({showAvgAnnualData: checked});
        //         // showAverageAnnualTimeSeriesAtLocation(activeLocation.point, activeLocation.coord);
        //     }, 500));

        d3.select("#capture").on("click", function () {
            var promise = new window.Promise(function (resolve, reject) {
                resolve(loadScreenCaptureImage());
            });

            promise.then(function (imageBuffer) {
                if (0 === imageBuffer.byteLength) {
                    report.error({status: -1});
                } else {
                    var exportedFile = "screencapture.png";
                    var blob = new Blob([imageBuffer], {type: "image/png;"});
                    if (navigator.msSaveBlob) { // IE 10+
                        navigator.msSaveBlob(blob, exportedFile);
                    } else {
                        var a = document.createElement("a");
                        if (a.download !== undefined) {
                            var url = URL.createObjectURL(blob);
                            a.setAttribute("href", url);
                            a.setAttribute("download", exportedFile);
                            a.style.visibility = "hidden";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                    }
                }
            });
        });
    }

    function fitView(newView) {
        newView = newView === undefined ? view : newView;
        d3.selectAll(".fill-screen").attr("width", newView.width).attr("height", newView.height);
        var canvas = d3.select("#map");
        var context = canvas.node().getContext("2d");
        var devicePixelRatio = window["devicePixelRatio"] || 1,
            backingStoreRatio = context.webkitBackingStorePixelRatio ||
                context.mozBackingStorePixelRatio ||
                context.msBackingStorePixelRatio ||
                context.oBackingStorePixelRatio ||
                context.backingStorePixelRatio || 1,
            ratio = devicePixelRatio / backingStoreRatio;
        if (!isNaN(ratio) && ratio >= 2) {
            ratio = 1.8;
        }
        d3.selectAll(".fit-screen").style("width", "".concat(newView.width, "px"))
            .style("height", "".concat(newView.height, "px"))
            .attr("width", newView.width * ratio)
            .attr("height", newView.height * ratio).each(function () {
            this.getContext("2d").scale(ratio, ratio);
        });

        // d3.select("#hmenu").style("max-height", "".concat(window.innerHeight - 20, "px"));
    }

    function orientationChanged() {
        var timeout = 120;
        return new window.Promise(function (resolve, reject) {
            var go = function (i, height0) {
                window.innerHeight != height0 || i > timeout ?
                    resolve() : window.requestAnimationFrame(function () {
                        go(i + 1, height0)
                    });
            };
            go(0, window.innerHeight);
        });
    }

    function checkWebGL(canvas) {
        var gl;

        try {
            gl = canvas.node().getContext("webgl");
        } catch (e) {
        }

        if (!gl) {
            try {
                gl = canvas.node().getContext("experimental-webgl");
            } catch (e) {
            }
        }

        // var extensions = gl.getExtension("WEBGL_depth_texture") || gl.getExtension("MOZ_WEBGL_depth_texture") || gl.getExtension("WEBKIT_WEBGL_depth_texture");

        // if(extensions === null){
        //     return undefined;
        // }

        return gl || undefined;
    }

    var isolineThresholdsCallback = _.debounce(function () {
        overlayAgent.submit(drawOverlay, fieldAgent.value(),
            configuration.get("overlayType"), configuration.get("showContour"), configuration.get("showContourLabel"), configuration.get("showWindVector"));
    }, 1000);

    // function drawPredictDays(maxProductPeriod) {
    //     let predictDays = d3.select("#predict-days-progress");
    //     µ.clearCanvas(predictDays.node());
    //     // let width = d3.select("#predict-days").node().clientWidth;
    //     let width = d3.select("#hmenu-nani-slide").node().clientWidth;
    //     let height = d3.select("#hmenu-nani-slide").node().clientHeight;
    //
    //     predictDays.attr("width", width);
    //     predictDays.attr("height", height);
    //     let c = predictDays.node(), g = c.getContext("2d");
    //
    //     // days line
    //     let productPeriodUnit = "년";
    //     let moveUnit = 50;
    //     let periodUnit = moveUnit / (maxProductPeriod);
    //     let widthUnit = (width - 50) * periodUnit;
    //     let initProductPeriodDay = 1850;
    //
    //     g.save();
    //     g.font = "0.8em Arial";
    //     g.fillStyle = "rgba(255,255,255,1.0)";
    //
    //     for (let k = 10, p = initProductPeriodDay; k <= width; k += widthUnit, p += moveUnit) {
    //         g.fillText(p + productPeriodUnit, k, height - 7);
    //     }
    //     g.restore();
    // }

    function timeBoxUpdate(toggleFlag) {
            if (toggleFlag) d3.select("#data-date").classed("local", !d3.select("#data-date").classed("local"));
            showDate(gridAgent.cancel.requested ? null : gridAgent.value());
    }

    function crossSectionOverlayReady() {
        let width = view.width, height = view.height;
        let canvas = d3.select("#crossSection2d");
        canvas.width = width * 0.58;
        canvas.height = height * 0.47;

        if (!checkWebGL(canvas)) {
            return;
        }

        crossSectionOverlayAgent.submit(mcOverlayCs, canvas, configuration);
    };

    function downloadAverageAnnualTimeSeries(coords) {
        var promise = new window.Promise(function (resolve, reject) {
            resolve(loadAverageVicennialTimeSeriesDataMultipleOverlay());
        });

        promise.then(function (climateDataMultipleOverlay) {
            var langCode = d3.select("body").attr("data-lang") || "en";
            var items = [], items$1 = [] /* wind */, items$2 = [] /* overlay */;
            var headers = [],
                primaryUnit = climateDataMultipleOverlay.header.primary.units, overlayUnits = [];
            for (var i = 0; i < climateDataMultipleOverlay.header.overlays.length; i++) {
                overlayUnits.push(climateDataMultipleOverlay.header.overlays[i].units);
            }

            // Header
            // headers.push(d3.select("#location-info-date-name")._groups[0][0].innerText);
            headers.push('시간');
            for (var i = 0; i < climateDataMultipleOverlay.data.length; i++) {
                headers.push(climateDataMultipleOverlay.data[i].date);
            }
            headers.push('위치 정보');
            // Primary
            const param = configuration.get("param") === 'wind' ? '풍속': '해류';
            items$1.push(param + " [" + climateDataMultipleOverlay.header.primary.units[0].label + "]");

            for (var i = 0; i < climateDataMultipleOverlay.data.length; i++) {
                items$1.push(formatValue(climateDataMultipleOverlay.data[i].primary.interpolates[2], primaryUnit[0]));
            }
            // items$1.push("\"" + d3.select("#location-info-coord")._groups[0][0].innerText.replace("\n", ", ") + "\"");
            items$1.push(coords);
            items.push(items$1);

            var overlay = "";
            for (var i = 0; i < climateDataMultipleOverlay.header.overlays.length; i++) {
                items$2 = [];
                var desc = climateDataMultipleOverlay.header.overlays[i].description;
                items$2.push((langCode === "en" ? desc.en : desc.kr)
                    + " [" + climateDataMultipleOverlay.header.overlays[i].units[0].label + "]");

                for (var j = 0; j < climateDataMultipleOverlay.data.length; j++) {
                    items$2.push(formatValue(climateDataMultipleOverlay.data[j].overlays[i].interpolates, overlayUnits[i][0]));
                }
                items.push(items$2);
            }
            exportToCSV("20-year average, 1860-2100", headers, items, "data.xlsx");
        });

        function formatValue(value, units) {
            return parseFloat(value).toFixed(units.precision > 2 ? 2 : units.precision);
        }
    };

    function exportToCSV(title, headers, items, filename) {
        if (headers) {
            items.unshift(headers);
        }

        var wb = XLSX.utils.book_new();
        wb.Props = {
            Title: title,
            Subject: "timeseries",
            Author: "NIMS",
            CreatedDate: new Date()
        };

        wb.SheetNames.push(title);
        var ws_data = items;
        var ws = XLSX.utils.aoa_to_sheet(ws_data);
        wb.Sheets[title] = ws;
        var wbout = XLSX.write(wb, {bookType: "xlsx", type: "binary"});

        function s2ab(s) {
            var buf = new ArrayBuffer(s.length);
            var view = new Uint8Array(buf);
            for (var i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
            return buf;
        }

        var exportedFile = filename || "export-csv.xlsx";

        var blob = new Blob([s2ab(wbout)], {type: "application/octet-stream;charset=utf-8;"});
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, exportedFile);
        } else {
            var a = document.createElement("a");
            if (a.download !== undefined) {
                var url = URL.createObjectURL(blob);
                a.setAttribute("href", url);
                a.setAttribute("download", exportedFile);
                a.style.visibility = "hidden";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        }
    }

    function isInsideBoundary(point) {
            if (fieldAgent.value() && fieldAgent.value().isInsideBoundary(point[0], point[1])) return true;  // outside the field boundary, so ignore.
            else return false;
    }

    function colorBarUnitUpdate(units) {
        const grid = (gridAgent.value() || {}).overlayGrid;
        if (!grid) return;
        const scale = grid.scale;
        const bounds = scale.bounds;
        var colorBar = d3.select("#scale");
        const scaleLabel = d3.select("#scale_label");
        var c = colorBar.node();
        colorBar.on("mousemove", null);
        colorBar.on("mousemove", function () {
            if (!µ.isMobile()) {
                const n = c.height - 1;
                var y = d3.mouse(this)[1];
                var pct = µ.clamp((Math.round(y) - 2) / (n - 2), 0, 1);
                var value = µ.spread(1 - pct, bounds[0], bounds[1]);

                colorBar.attr("title", µ.formatScalar(value, units) + " " + units.label);
            } else {
                const n = c.width - 1;
                var x = d3.mouse(this)[0];
                var pct = µ.clamp((Math.round(x) - 2) / (n - 2), 0, 1);
                var value = µ.spread(pct, bounds[0], bounds[1]);
                scaleLabel.style("left", d3.mouse(this)[0] - 20 + 'px');
                scaleLabel.style("display", 'inherit');
                scaleLabel.node().innerText = µ.formatScalar(value, units);
            }
        });
        scaleLabel.style("display", 'none'); //현재 열려있는 컬러바말풍선을 안보이게 한다
    }

    const getCurrentValue = (coord) => {
        const grid = (gridAgent.value() || {}).overlayGrid;
        if (!grid) return null;
        let value = grid.interpolate(coord[0], coord[1]);
        if (_.isArray(value)) value = value[2];
        const gradient = grid.scale.gradient(value, 1)
        return {gradient, value};
    }

    function checkLocationInFrontGlobe(projection, coord) {
        let centerCoord = projection.invert([view.width / 2, view.height / 2]);
        let distance = d3.geoDistance(coord, centerCoord);
        if (configuration.get("projection") === "orthographic" && distance > 1.57) return false;

        return true;
    }

    const drawBasicLocationPoint = (units) => {
        //클릭하지 않았을때는 activeLocation에 값이 없다
        if (!(activeLocation && activeLocation.point && activeLocation.coord)) return;
        if (!units) {
            // unit을 인자로 안줬을때는 activateLocation에 unit이 있는지 확인한다.
            // activateLocation에 unit이 있는 경우는 현재 클릭은 한 상태이고 시간이나 단위가 변해서
            // 클릭 지점은 같지만 표현 value가 달라진 경우이다.
            // activateLocation에 unit이 없는 경우는 현재 최초로 클릭을 누룬 상태이다.
            // 이 경우는 현재 변수의 unitList중에 첫번째로 unit을 적용한다
            let overlayType = configuration.get("overlayType");
            if (overlayType === 'default') overlayType = 'wind';
            units = activeLocation.units ? activeLocation.units : products.getUnitList(overlayType)[0];
        }

        let point = activeLocation.point; //activeLocation은 클릭하는 순간 저장됨
        let coord = activeLocation.coord;
        var locationPointContext = d3.select("#locationpoint").node().getContext("2d");
        let globe = globeAgent.value();
        let pathPoint = globe.projection(activeLocation.coord);
        µ.clearCanvas(d3.select("#locationpoint").node());
        const currentValue = getCurrentValue(coord); //해당 좌표의 데이터를 grid에서 얻어옴
        if (checkLocationInFrontGlobe(globe.projection, coord)) {
            //클릭한 지점의 원을 그림
            locationPointContext.strokeStyle = "rgba(0,255,0,1.0)";
            locationPointContext.lineWidth = 2.5;
            locationPointContext.beginPath();
            locationPointContext.arc(pathPoint[0], pathPoint[1], 5, 0, Math.PI * 2, true);
            locationPointContext.stroke();
            if (currentValue.value) { // 기본모드는 클릭한 지점에 현재 값을 보여줘야함
                locationPointContext.font = "16pt Fira";
                locationPointContext.strokeStyle = "white";
                locationPointContext.lineWidth = 0.8;
                locationPointContext.strokeText(units.conversion(currentValue.value).toFixed(units.precision), pathPoint[0], pathPoint[1])

                locationPointContext.fillStyle = 'rgba(' + currentValue.gradient[0] + "," + currentValue.gradient[1] + "," + currentValue.gradient[2] + "," + currentValue.gradient[3] + ')'
                locationPointContext.fillText(units.conversion(currentValue.value).toFixed(units.precision), pathPoint[0], pathPoint[1])
            }
        }
        activeLocation = {point: point, coord: coord, units: units};
    }

    function start() {
        // Everything is now set up, so load configuration from the hash fragment and kick off change events.
        configuration.fetch();
        return {
            configuration: configuration,
            inputController,
            navigate: navigate,
            stopCurrentAnimation: stopCurrentAnimation,
            isolineThresholdsCallback: isolineThresholdsCallback,
            clearAvgAnnualLocationDetails: clearAvgAnnualLocationDetails,
            clearLocationDetailsCrossSection: clearLocationDetailsCrossSection,
            clearLocationDetailsAreaMean: clearLocationDetailsAreaMean,
            renderCrossSectionOverlay: renderCrossSectionOverlay,
            // drawPredictDays: drawPredictDays,
            timeBoxUpdate,
            // showLocationDetails,
            crossSectionOverlayReady,
            showTimeSeriesOverlayChart,
            downloadAverageAnnualTimeSeries,
            isInsideBoundary,
            colorBarUnitUpdate,
            drawBasicLocationPoint,
            report: report,
        };
    }

    return when(true).then(init).then(loadEarthImage(earthPath)).then(loadEarthImage(spacePath)).then(start).otherwise(report.error);

};

